import React, { Component } from 'react';
import * as validators from 'utils/validators';
import InputField from 'components/InputField';
import { Field, change } from 'redux-form';
import CollapsablePanel from './CollapsePanel';
import ColorPickerField from 'components/ColorPickerField';
import ThemePreview from '../components/ThemePreview';
import PhotoField from 'components/PhotoField';
import ShopifyLocationsSelectField from 'components/ShopifyLocationsField';
import { LANGUAGE_PRESETS, LABEL_FIELDS, defaultLabels } from '../../config/template_labels';
import { classifyIntent, buildLLMMessages, parseLLMResponse, applyProposal, executeDeterministicAction as execAction, createSnapshot, decompressSnapshot, COLOR_PALETTES, RAG_EXAMPLES, isLiquidTemplate } from '../copilot/CopilotEngine';
import { canUseFeature } from '../../config/pricing';

const COUNTRIES = [
    { code: 'US', name: 'United States' }, { code: 'GB', name: 'United Kingdom' }, { code: 'CA', name: 'Canada' },
    { code: 'AU', name: 'Australia' }, { code: 'DE', name: 'Germany' }, { code: 'FR', name: 'France' },
    { code: 'ES', name: 'Spain' }, { code: 'IT', name: 'Italy' }, { code: 'NL', name: 'Netherlands' },
    { code: 'BE', name: 'Belgium' }, { code: 'AT', name: 'Austria' }, { code: 'CH', name: 'Switzerland' },
    { code: 'PT', name: 'Portugal' }, { code: 'IE', name: 'Ireland' }, { code: 'SE', name: 'Sweden' },
    { code: 'DK', name: 'Denmark' }, { code: 'NO', name: 'Norway' }, { code: 'FI', name: 'Finland' },
    { code: 'PL', name: 'Poland' }, { code: 'CZ', name: 'Czech Republic' }, { code: 'SK', name: 'Slovakia' },
    { code: 'HU', name: 'Hungary' }, { code: 'RO', name: 'Romania' }, { code: 'BG', name: 'Bulgaria' },
    { code: 'HR', name: 'Croatia' }, { code: 'SI', name: 'Slovenia' }, { code: 'RS', name: 'Serbia' },
    { code: 'GR', name: 'Greece' }, { code: 'TR', name: 'Turkey' }, { code: 'UA', name: 'Ukraine' },
    { code: 'LT', name: 'Lithuania' }, { code: 'LV', name: 'Latvia' }, { code: 'EE', name: 'Estonia' },
    { code: 'IN', name: 'India' }, { code: 'NZ', name: 'New Zealand' }, { code: 'SG', name: 'Singapore' },
    { code: 'JP', name: 'Japan' }, { code: 'CN', name: 'China' }, { code: 'AE', name: 'United Arab Emirates' },
    { code: 'SA', name: 'Saudi Arabia' }, { code: 'IL', name: 'Israel' }, { code: 'ZA', name: 'South Africa' },
    { code: 'BR', name: 'Brazil' }, { code: 'MX', name: 'Mexico' }
];

class CodeEditor extends Component {
    onChange(value) {
        this.props.input.onChange(value);
    }

    formatCode() {
        if (!this.aceEditor) return;
        const val = this.props.input.value;
        if (!val) return;

        try {
            let formatted = this.formatHtmlWithCss(val);
            this.props.input.onChange(formatted);
        } catch (e) {
            try {
                const beautify = require('brace/ext/beautify');
                beautify.beautify(this.aceEditor.editor.session);
            } catch (ex) {}
        }
    }

    formatHtmlWithCss(html) {
        let parts = [];
        let regex = /(<style[^>]*>)([\s\S]*?)(<\/style>)/gi;
        let lastIndex = 0;
        let match;

        while ((match = regex.exec(html)) !== null) {
            if (match.index > lastIndex) {
                parts.push({ type: 'html', content: html.slice(lastIndex, match.index) });
            }
            parts.push({ type: 'css', content: match[2], open: match[1], close: match[3] });
            lastIndex = regex.lastIndex;
        }
        if (lastIndex < html.length) {
            parts.push({ type: 'html', content: html.slice(lastIndex) });
        }

        let result = parts.map(p => {
            if (p.type === 'css') {
                return p.open + '\n' + this.formatCss(p.content) + '\n' + p.close;
            }
            return this.formatHtml(p.content);
        }).join('\n');

        return result.replace(/\n{3,}/g, '\n\n').trim();
    }

    formatCss(css) {
        let lines = [];
        let current = '';
        let depth = 0;
        let inUrl = false;
        let inLiquid = false;

        for (let i = 0; i < css.length; i++) {
            let ch = css[i];
            let next = css[i + 1] || '';

            if (css.slice(i, i + 4) === 'url(') inUrl = true;
            if (inUrl && ch === ')') inUrl = false;

            if (ch === '{' && next === '%') inLiquid = true;
            if (ch === '%' && next === '}') { current += ch; i++; current += '}'; inLiquid = false; continue; }
            if (ch === '{' && next === '{') inLiquid = true;
            if (ch === '}' && css[i - 1] === '}' && inLiquid) { inLiquid = false; current += ch; continue; }

            if (inUrl || inLiquid) {
                current += ch;
                continue;
            }

            if (ch === '{') {
                current += ' {';
                lines.push({ text: current.trim(), indent: depth, type: 'open' });
                current = '';
                depth++;
            } else if (ch === '}') {
                if (current.trim()) {
                    lines.push({ text: current.trim(), indent: depth, type: 'prop' });
                }
                current = '';
                depth = Math.max(0, depth - 1);
                lines.push({ text: '}', indent: depth, type: 'close' });
            } else if (ch === ';' && depth > 0) {
                current += ';';
                lines.push({ text: current.trim(), indent: depth, type: 'prop' });
                current = '';
            } else if (ch === '\n' && depth === 0 && current.trim()) {
                lines.push({ text: current.trim(), indent: 0, type: 'line' });
                current = '';
            } else {
                current += ch;
            }
        }

        if (current.trim()) {
            lines.push({ text: current.trim(), indent: depth, type: 'line' });
        }

        return lines.map(l => '  '.repeat(l.indent) + l.text).join('\n');
    }

    formatHtml(html) {
        let formatted = '';
        let indent = 0;
        let tokens = html.replace(/>\s*</g, '>\n<').split('\n');

        let voidElements = ['br', 'hr', 'img', 'input', 'meta', 'link'];

        for (let i = 0; i < tokens.length; i++) {
            let line = tokens[i].trim();
            if (!line) continue;

            let isClosing = /^<\//.test(line);
            let tagMatch = line.match(/^<([a-zA-Z0-9-]+)/);
            let tagName = tagMatch ? tagMatch[1].toLowerCase() : '';
            let isVoid = voidElements.indexOf(tagName) !== -1;
            let isSelfClosing = /\/>$/.test(line) || isVoid;
            let isOpening = /^<[a-zA-Z]/.test(line) && !isClosing && !isSelfClosing;

            let hasClosingOnSameLine = isOpening && tagName && new RegExp('</' + tagName + '\\s*>\\s*$', 'i').test(line);

            if (isClosing) indent = Math.max(0, indent - 1);
            formatted += '  '.repeat(indent) + line + '\n';
            if (isOpening && !hasClosingOnSameLine) indent++;
        }

        return formatted;
    }

    undo() {
        if (this.aceEditor && this.aceEditor.editor) {
            this.aceEditor.editor.undo();
        }
    }

    redo() {
        if (this.aceEditor && this.aceEditor.editor) {
            this.aceEditor.editor.redo();
        }
    }

    render() {
        let { meta: { touched, error, warning } } = this.props;
        let AceEditor = <div />;
        try {
            require('brace');
            require('brace/mode/html');
            require('brace/theme/xcode');
            require('brace/ext/searchbox');
            require('brace/ext/language_tools');
            require('brace/ext/beautify');
            if (!this.editor)
                this.editor = require('react-ace').default;
        } catch (e) {}

        if (this.editor) {
            AceEditor = this.editor;
        }

        const templateCompleters = [
            { caption: 'invoice.number.series', value: '{{invoice.number.series}}', meta: 'Invoice series' },
            { caption: 'invoice.number.id', value: '{{invoice.number.id}}', meta: 'Invoice number' },
            { caption: 'invoice.date', value: '{{invoice.date}}', meta: 'Invoice date' },
            { caption: 'invoice.dueDate', value: '{{invoice.dueDate}}', meta: 'Due date' },
            { caption: 'invoice.currency.main', value: '{{invoice.currency.main}}', meta: 'Currency' },
            { caption: 'invoice.final.price', value: '{{invoice.final.price}}', meta: 'Total' },
            { caption: 'invoice.taxes.vta', value: '{{invoice.taxes.vta}}', meta: 'Tax amount' },
            { caption: 'total.subtotal', value: '{{total.subtotal}}', meta: 'Pre-discount subtotal' },
            { caption: 'total.discount', value: '{{total.discount}}', meta: 'Order discount' },
            { caption: 'total.hasDiscount', value: '{{#total.hasDiscount}}{{/total.hasDiscount}}', meta: 'Conditional' },
            { caption: 'discount', value: '{{discount}}', meta: 'Line discount (row)' },
            { caption: 'invoice.isPaid', value: '{{#invoice.isPaid}}{{/invoice.isPaid}}', meta: 'Conditional' },
            { caption: 'invoice.isCreditNote', value: '{{#invoice.isCreditNote}}{{/invoice.isCreditNote}}', meta: 'Conditional' },
            { caption: 'order.order_number', value: '{{order.order_number}}', meta: 'Order #' },
            { caption: 'order.created_at', value: '{{order.created_at}}', meta: 'Order date' },
            { caption: 'order.note', value: '{{order.note}}', meta: 'Notes' },
            { caption: 'order.shipping_address.name', value: '{{order.shipping_address.name}}', meta: 'Ship to' },
            { caption: 'order.shipping_address.address1', value: '{{order.shipping_address.address1}}', meta: 'Ship addr' },
            { caption: 'order.shipping_address.city', value: '{{order.shipping_address.city}}', meta: 'Ship city' },
            { caption: 'order.billing_address.name', value: '{{order.billing_address.name}}', meta: 'Bill to' },
            { caption: 'order.fulfillments', value: '{{#order.fulfillments}}{{tracking_number}}{{/order.fulfillments}}', meta: 'Tracking' },
            { caption: 'order.meta.<namespace>.<key>', value: '{{order.meta.custom.YOUR_FIELD}}', meta: 'Order metafield' },
            { caption: 'seller.company', value: '{{seller.company}}', meta: 'Seller' },
            { caption: 'seller.email', value: '{{seller.email}}', meta: 'Seller' },
            { caption: 'seller.phone', value: '{{seller.phone}}', meta: 'Seller' },
            { caption: 'seller.address.street', value: '{{seller.address.street}}', meta: 'Seller addr' },
            { caption: 'seller.address.city', value: '{{seller.address.city}}', meta: 'Seller addr' },
            { caption: 'seller.bank.iban', value: '{{seller.bank.iban}}', meta: 'Bank' },
            { caption: 'seller.bank.name', value: '{{seller.bank.name}}', meta: 'Bank' },
            { caption: 'seller.logo', value: '{{seller.logo}}', meta: 'Logo URL' },
            { caption: 'seller.colorPalette.color1', value: '{{seller.colorPalette.color1}}', meta: 'Color' },
            { caption: 'seller.colorPalette.color4', value: '{{seller.colorPalette.color4}}', meta: 'Color' },
            { caption: 'buyer.name', value: '{{buyer.name}}', meta: 'Buyer' },
            { caption: 'buyer.company', value: '{{buyer.company}}', meta: 'Buyer' },
            { caption: 'buyer.email', value: '{{buyer.email}}', meta: 'Buyer' },
            { caption: 'buyer.address.street', value: '{{buyer.address.street}}', meta: 'Buyer addr' },
            { caption: 'settings.showShipping', value: '{{#settings.showShipping}}{{/settings.showShipping}}', meta: 'Toggle' },
            { caption: 'settings.showBankDetails', value: '{{#settings.showBankDetails}}{{/settings.showBankDetails}}', meta: 'Toggle' },
            { caption: 'settings.showNotes', value: '{{#settings.showNotes}}{{/settings.showNotes}}', meta: 'Toggle' },
            { caption: 'settings.showTracking', value: '{{#settings.showTracking}}{{/settings.showTracking}}', meta: 'Toggle' },
            { caption: 'settings.showTerms', value: '{{#settings.showTerms}}{{/settings.showTerms}}', meta: 'Toggle' },
            { caption: 'settings.showPaidBadge', value: '{{#settings.showPaidBadge}}{{/settings.showPaidBadge}}', meta: 'Toggle' },
            { caption: 'settings.invoiceTitle', value: '{{settings.invoiceTitle}}', meta: 'Setting' },
            { caption: 'settings.thankYouText', value: '{{settings.thankYouText}}', meta: 'Setting' },
            { caption: 'settings.paymentTerms', value: '{{settings.paymentTerms}}', meta: 'Setting' },
            { caption: 'formatCurrency', value: '{{#formatCurrency}}{{invoice.final.price}}, en-US, USD{{/formatCurrency}}', meta: 'Helper' },
            { caption: 'formatDate', value: '{{#formatDate}}{{order.created_at}}, MMM Do YY{{/formatDate}}', meta: 'Helper' },
            { caption: 'description_rows', value: '{{{description_rows}}}', meta: 'Line items' },
        ];

        return <div>
            <div style={{marginBottom:'6px', display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                <div>
                    <button type="button" className="btn btn-default btn-xs" onClick={this.undo.bind(this)} title="Undo (Ctrl+Z)">
                        <i className="fa fa-undo"></i>
                    </button>
                    <button type="button" className="btn btn-default btn-xs" style={{marginLeft:'2px'}} onClick={this.redo.bind(this)} title="Redo (Ctrl+Shift+Z)">
                        <i className="fa fa-repeat"></i>
                    </button>
                </div>
                <button type="button" className="btn btn-default btn-xs" onClick={this.formatCode.bind(this)}>
                    <i className="fa fa-indent"></i> Format Code
                </button>
            </div>
            <AceEditor
            mode="html"
            theme="xcode"
            name={"code-editor-" + (this.props.id || 'default')}
            onChange={this.onChange.bind(this)}
            fontSize={12}
            showPrintMargin={false}
            showGutter={true}
            highlightActiveLine={true}
            width="100%"
            defaultValue={this.props.input.value}
            value={this.props.input.value}
            ref={(ref) => { this.aceEditor = ref; if (this.props.editorRef) this.props.editorRef(ref); }}
            onLoad={(editor) => {
                editor.getSession().setUseWorker(false);
                editor.completers = [{ getCompletions: function(editor, session, pos, prefix, callback) {
                    callback(null, templateCompleters.filter(c => c.caption.indexOf(prefix) !== -1 || c.meta.toLowerCase().indexOf(prefix.toLowerCase()) !== -1));
                }}];
            }}
            setOptions={{
                enableBasicAutocompletion: true,
                enableLiveAutocompletion: true,
                enableSnippets: true,
                showLineNumbers: true,
                tabSize: 2,
            }} />
            {touched &&
                ((error && <span className="label label-danger">{error}</span>) ||
                    (warning && <span className="label label-warning">{warning}</span>))}
        </div>;
    }
}

const FONT_OPTIONS = [
    { value: "'DM Sans', sans-serif", label: "DM Sans", category: "Sans-serif" },
    { value: "'Inter', sans-serif", label: "Inter", category: "Sans-serif" },
    { value: "'Source Sans 3', sans-serif", label: "Source Sans", category: "Sans-serif" },
    { value: "'IBM Plex Sans', sans-serif", label: "IBM Plex Sans", category: "Sans-serif" },
    { value: "'Roboto', sans-serif", label: "Roboto", category: "Sans-serif" },
    { value: "'Lato', sans-serif", label: "Lato", category: "Sans-serif" },
    { value: "'Open Sans', sans-serif", label: "Open Sans", category: "Sans-serif" },
    { value: "'Nunito', sans-serif", label: "Nunito", category: "Sans-serif" },
    { value: "'Montserrat', sans-serif", label: "Montserrat", category: "Sans-serif" },
    { value: "'Poppins', sans-serif", label: "Poppins", category: "Sans-serif" },
    { value: "'Raleway', sans-serif", label: "Raleway", category: "Sans-serif" },
    { value: "'Barlow Condensed', sans-serif", label: "Barlow Condensed", category: "Condensed" },
    { value: "'Oswald', sans-serif", label: "Oswald", category: "Condensed" },
    { value: "'Syne', sans-serif", label: "Syne", category: "Display" },
    { value: "'Playfair Display', serif", label: "Playfair Display", category: "Serif" },
    { value: "'Libre Baskerville', serif", label: "Libre Baskerville", category: "Serif" },
    { value: "'Cormorant Garamond', serif", label: "Cormorant Garamond", category: "Serif" },
    { value: "'Lora', serif", label: "Lora", category: "Serif" },
    { value: "'Merriweather', serif", label: "Merriweather", category: "Serif" },
    { value: "'PT Serif', serif", label: "PT Serif", category: "Serif" },
    { value: "'JetBrains Mono', monospace", label: "JetBrains Mono", category: "Monospace" },
    { value: "'Fira Code', monospace", label: "Fira Code", category: "Monospace" },
    { value: "'Space Mono', monospace", label: "Space Mono", category: "Monospace" },
];


const DATE_FORMATS = [
    { value: "DD/MM/YYYY", label: "31/12/2024 (DD/MM/YYYY)" },
    { value: "MM/DD/YYYY", label: "12/31/2024 (MM/DD/YYYY)" },
    { value: "YYYY-MM-DD", label: "2024-12-31 (YYYY-MM-DD)" },
    { value: "MMM Do, YYYY", label: "Dec 31st, 2024" },
    { value: "D MMMM YYYY", label: "31 December 2024" },
    { value: "DD.MM.YYYY", label: "31.12.2024 (DD.MM.YYYY)" },
];

class Theme extends Component {

    t(id) {
        return this.props.intl ? this.props.intl.formatMessage({ id }) : id;
    }

    constructor(props) {
        super(props);
        this.state = {
            activeTab: 'style',
            snippetPopover: null,
            copilotInput: '',
            copilotMessages: [],
            copilotLoading: false,
            copilotEngineReady: false,
            copilotEngineLoading: false,
            copilotError: null,
            copilotProposal: null,
            copilotCanUndo: false,
            copilotCanRedo: false,
            copilotModel: 'Qwen3-8B-q4f16_1-MLC',
            copilotShowModelConfirm: false,
            copilotPendingLLMInput: null,
        };
        this.copilotWorker = null;
        this.embeddingsWorker = null;
        this.embeddingsReady = false;
        this.copilotHistory = [];
        this.copilotHistoryIndex = -1;
    }

    componentDidMount(){
        window.addEventListener('message', (e) => {
            let {theme, dispatch, el} = this.props;

            if (e.data.theme === theme.name && e.data.source === "template-editor"){
                let curateHtml = (html)=> html.replaceAll(" contenteditable=\"true\"", "").trim();

                if (e.data.type === "text"){
                    theme.bodyTemplate = theme.bodyTemplate.replaceAll(curateHtml(e.data.initialHtml), curateHtml(e.data.newHtml));
                }

                if (e.data.type === "delete"){
                    theme.bodyTemplate = theme.bodyTemplate.replaceAll(curateHtml(e.data.initialHtml), "");
                }

                if (e.data.type == "break-line"){
                    theme.bodyTemplate = theme.bodyTemplate.replaceAll(curateHtml(e.data.initialHtml), `${curateHtml(e.data.initialHtml)}<br data-editable="true" />`);
                }

                dispatch(change("Profile", `${el}.bodyTemplate`, theme.bodyTemplate));
            }

        }, false);
    }

    setTab(tab) {
        this.setState({ activeTab: tab });
        if (tab === 'copilot') {
            if (this.copilotHistory.length === 0) {
                this.saveCopilotSnapshot();
            }
        }
    }

    setSetting(key, value) {
        const { dispatch, el } = this.props;
        dispatch(change("Profile", `${el}.settings.${key}`, value));
    }

    applyPalette(colors) {
        const { dispatch, el } = this.props;
        dispatch(change("Profile", `${el}.colorPalette.color1`, colors[0]));
        dispatch(change("Profile", `${el}.colorPalette.color2`, colors[1]));
        dispatch(change("Profile", `${el}.colorPalette.color3`, colors[2]));
        dispatch(change("Profile", `${el}.colorPalette.color4`, colors[3]));
        dispatch(change("Profile", `${el}.colorPalette.color5`, colors[4]));
    }

    renderStyleTab() {
        const { el, theme, user } = this.props;
        const t = this.t.bind(this);
        const settings = theme.settings || {};

        return <div className="editor-tab-content">
            <div className="editor-section">
                <div className="editor-section-title">{t('templateEditor.logo')}</div>
                <Field name={`${el}.logo`} websiteId={`${user.shopify.shopName}`} component={PhotoField} />
            </div>

            <div className="editor-section">
                <div className="editor-section-title">{t('templateEditor.colorPalette')}</div>
                <div className="palette-strip">
                    {COLOR_PALETTES.map(p => {
                        const isActive = theme.colorPalette && theme.colorPalette.color1 === p.colors[0] && theme.colorPalette.color4 === p.colors[3];
                        return <div
                            key={p.name}
                            className={'palette-chip' + (isActive ? ' active' : '')}
                            title={p.name}
                            onClick={() => this.applyPalette(p.colors)}
                        >
                            {p.colors.map((c, i) => (
                                <span key={i} style={{ background: c }}></span>
                            ))}
                        </div>;
                    })}
                </div>
                <div className="color-pickers-inline">
                    <div className="color-picker-item">
                        <span className="color-label">{t('templateEditor.colorText')}</span>
                        <Field name={`${el}.colorPalette.color1`} type="text" component={ColorPickerField} label="" color={theme.colorPalette.color1} />
                    </div>
                    <div className="color-picker-item">
                        <span className="color-label">{t('templateEditor.colorBg')}</span>
                        <Field name={`${el}.colorPalette.color2`} type="text" component={ColorPickerField} label="" color={theme.colorPalette.color2} />
                    </div>
                    <div className="color-picker-item">
                        <span className="color-label">{t('templateEditor.colorBorder')}</span>
                        <Field name={`${el}.colorPalette.color3`} type="text" component={ColorPickerField} label="" color={theme.colorPalette.color3} />
                    </div>
                    <div className="color-picker-item">
                        <span className="color-label">{t('templateEditor.colorPrimary')}</span>
                        <Field name={`${el}.colorPalette.color4`} type="text" component={ColorPickerField} label="" color={theme.colorPalette.color4} />
                    </div>
                    <div className="color-picker-item">
                        <span className="color-label">{t('templateEditor.colorAccent')}</span>
                        <Field name={`${el}.colorPalette.color5`} type="text" component={ColorPickerField} label="" color={theme.colorPalette.color5} />
                    </div>
                </div>
            </div>

            <div className="editor-section">
                <div className="editor-section-title">{t('templateEditor.font')}</div>
                <Field name={`${el}.settings.font`} className="form-control" component="select">
                    <option value="">{t('templateEditor.fontDefault')}</option>
                    {FONT_OPTIONS.map(f => (
                        <option key={f.value} value={f.value}>{f.label} — {f.category}</option>
                    ))}
                </Field>
            </div>

            <div className="editor-section">
                <div className="editor-section-title">{t('templateEditor.fontSize')}</div>
                <Field name={`${el}.settings.fontSize`} className="form-control" component="select">
                    <option value="">{t('templateEditor.fontSizeDefault')}</option>
                    <option value="9">9px — {t('templateEditor.fontSizeCompact')}</option>
                    <option value="10">10px — {t('templateEditor.fontSizeSmall')}</option>
                    <option value="11">11px — {t('templateEditor.fontSizeMediumSmall')}</option>
                    <option value="11.5">11.5px — {t('templateEditor.fontSizeDefault')}</option>
                    <option value="12">12px — {t('templateEditor.fontSizeMedium')}</option>
                    <option value="13">13px — {t('templateEditor.fontSizeMediumLarge')}</option>
                    <option value="14">14px — {t('templateEditor.fontSizeLarge')}</option>
                    <option value="15">15px — {t('templateEditor.fontSizeExtraLarge')}</option>
                </Field>
            </div>

            <div className="editor-section">
                <div className="editor-section-title">{t('templateEditor.documentTitle')}</div>
                <Field name={`${el}.settings.invoiceTitle`} className="form-control" component={InputField} type="text" placeholder={t('templateEditor.documentTitlePlaceholder')} />
            </div>
        </div>;
    }

    renderContentTab() {
        const { el, theme } = this.props;
        const t = this.t.bind(this);
        const settings = theme.settings || {};
        const body = (theme.bodyTemplate || '') + (theme.rowTemplate || '');
        const supports = (key) => body.indexOf(`settings.${key}`) !== -1;
        const hasConditionals = body.indexOf('settings.') !== -1;

        const sectionToggles = [
            { key: 'showShipping', label: t('templateEditor.shippingAddress') },
            { key: 'showBankDetails', label: t('templateEditor.bankPaymentDetails') },
            { key: 'showPaidBadge', label: t('templateEditor.paymentStatusBadge') },
            { key: 'showNotes', label: t('templateEditor.orderNotes') },
            { key: 'showTracking', label: t('templateEditor.trackingFulfillment') },
            { key: 'showTerms', label: t('templateEditor.termsConditions') },
            { key: 'showSignatureLines', label: t('templateEditor.signatureLines') },
            { key: 'showQrCode', label: t('templateEditor.qrCode') },
        ].filter(tog => supports(tog.key));

        const lineItemToggles = [
            { key: 'showTaxColumns', label: t('templateEditor.taxColumns') },
            { key: 'showSku', label: t('templateEditor.productSku') },
            { key: 'showImages', label: t('templateEditor.productImages') },
            { key: 'showDiscount', label: t('templateEditor.discountBreakdown') },
            { key: 'showWeight', label: t('templateEditor.itemWeight') },
            { key: 'showHsCode', label: t('templateEditor.hsCode') },
        ].filter(tog => supports(tog.key));

        const totalToggles = [
            { key: 'showSubtotal', label: t('templateEditor.subtotalBeforeTax') },
            { key: 'showTaxBreakdown', label: t('templateEditor.taxBreakdownByRate') },
            { key: 'showShippingCost', label: t('templateEditor.shippingCostSeparate') },
            { key: 'showDueDate', label: t('templateEditor.dueDate') },
        ].filter(tog => supports(tog.key));

        return <div className="editor-tab-content">
            {!hasConditionals && <div style={{padding:'8px 12px',background:'#fffbeb',border:'1px solid #fde68a',borderRadius:'4px',marginBottom:'14px',fontSize:'11px',color:'#92400e'}}>
                {t('templateEditor.noToggleSupport')}
            </div>}

            {sectionToggles.length > 0 && <div className="editor-section">
                <div className="editor-section-title">{t('templateEditor.sections')}</div>
                <div className="toggle-list">
                    {sectionToggles.map(t => <label key={t.key} className="toggle-item">
                        <Field name={`${el}.settings.${t.key}`} component="input" type="checkbox" />
                        <span>{t.label}</span>
                    </label>)}
                </div>
            </div>}

            <div className="editor-section">
                <div className="editor-section-title">{t('templateEditor.lineItems')}</div>
                <div className="toggle-list">
                    {lineItemToggles.map(tog => <label key={tog.key} className="toggle-item">
                        <Field name={`${el}.settings.${tog.key}`} component="input" type="checkbox" />
                        <span>{tog.label}</span>
                    </label>)}
                    <label className="toggle-item">
                        <Field name={`${el}.settings.mergeDiscounts`} component="input" type="checkbox" />
                        <span>{t('templateEditor.mergeDiscounts')}</span>
                    </label>
                    <label className="toggle-item">
                        <Field name={`${el}.settings.showOrderDiscountAsSummary`} component="input" type="checkbox" />
                        <span>{t('templateEditor.showOrderDiscountAsSummary')}</span>
                    </label>
                </div>
            </div>

            {totalToggles.length > 0 && <div className="editor-section">
                <div className="editor-section-title">{t('templateEditor.totals')}</div>
                <div className="toggle-list">
                    {totalToggles.map(tog => <label key={tog.key} className="toggle-item">
                        <Field name={`${el}.settings.${tog.key}`} component="input" type="checkbox" />
                        <span>{tog.label}</span>
                    </label>)}
                </div>
            </div>}

            <div className="editor-section">
                <div className="editor-section-title">{t('templateEditor.formatting')}</div>
                <label>{t('templateEditor.dateFormat')}</label>
                <Field name={`${el}.settings.dateFormat`} className="form-control" component="select">
                    {DATE_FORMATS.map(f => (
                        <option key={f.value} value={f.value}>{f.label}</option>
                    ))}
                </Field>
                <br/>
                <label>{t('templateEditor.decimalPlaces')}</label>
                <Field name={`${el}.settings.decimalPlaces`} className="form-control" component="select">
                    <option value="0">0 (100)</option>
                    <option value="2">2 (100.00)</option>
                    <option value="3">3 (100.000)</option>
                </Field>
                <br/>
                <label>{t('templateEditor.paymentTerms')}</label>
                <Field name={`${el}.settings.paymentTerms`} className="form-control" component="select">
                    <option value="">{t('templateEditor.paymentTermsNone')}</option>
                    <option value="Due on receipt">Due on receipt</option>
                    <option value="Net 7">Net 7</option>
                    <option value="Net 14">Net 14</option>
                    <option value="Net 30">Net 30</option>
                    <option value="Net 60">Net 60</option>
                    <option value="Net 90">Net 90</option>
                </Field>
                <br/>
                <label>{t('templateEditor.thankYouMessage')}</label>
                <Field name={`${el}.settings.thankYouText`} className="form-control" component="textarea" rows="2" placeholder={t('templateEditor.thankYouPlaceholder')} />
            </div>
        </div>;
    }

    applyLanguagePreset(langCode) {
        const { dispatch, el } = this.props;
        const preset = LANGUAGE_PRESETS.find(p => p.code === langCode);
        if (!preset) return;
        Object.keys(preset.labels).forEach(key => {
            dispatch(change("Profile", `${el}.labels.${key}`, preset.labels[key]));
        });
        if (preset.direction) {
            dispatch(change("Profile", `${el}.labels._direction`, preset.direction));
        } else {
            dispatch(change("Profile", `${el}.labels._direction`, ''));
        }
        if (preset.font) {
            dispatch(change("Profile", `${el}.settings.font`, preset.font));
        }
    }

    renderTranslationsTab() {
        const { el, theme, user } = this.props;
        const t = this.t.bind(this);
        const labels = theme.labels || {};

        return <div className="editor-tab-content">
            {!user.hasMultipleLocations && <div className="editor-section">
                <div className="editor-section-title">{t('templateEditor.countryMatchTitle')}</div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'8px'}}>
                    <div>
                        <label style={{fontSize:'11px',color:'#64748b',marginBottom:'4px',display:'block'}}>{t('templateEditor.addressType')}</label>
                        <Field className="form-control" name={`${el}.countryMatch`} component="select">
                            <option value="">{t('templateEditor.addressDisabled')}</option>
                            <option value="shipping_address.country_code">{t('templateEditor.addressShipping')}</option>
                            <option value="billing_address.country_code">{t('templateEditor.addressBilling')}</option>
                        </Field>
                    </div>
                    <div>
                        <label style={{fontSize:'11px',color:'#64748b',marginBottom:'4px',display:'block'}}>{t('templateEditor.country')}</label>
                        <Field className="form-control" name={`${el}.countryCodes`} component="select" disabled={!theme.countryMatch}>
                            <option value="">{t('templateEditor.anyCountry')}</option>
                            {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                        </Field>
                    </div>
                </div>
                <small style={{color:'#64748b',marginTop:'6px',display:'block'}}>{t('templateEditor.countryMatchDesc')}</small>
            </div>}

            <div className="editor-section">
                <div className="editor-section-title">{t('templateEditor.languagePreset')}</div>
                <select className="form-control" onChange={(e) => this.applyLanguagePreset(e.target.value)} defaultValue="">
                    <option value="" disabled>{t('templateEditor.selectLanguage')}</option>
                    {LANGUAGE_PRESETS.map(p => (
                        <option key={p.code} value={p.code}>{p.name}</option>
                    ))}
                </select>
                <small style={{color:'#64748b',marginTop:'4px',display:'block'}}>{t('templateEditor.languagePresetDesc')}</small>
            </div>

            {LABEL_FIELDS.map(group => (
                <div className="editor-section" key={group.category}>
                    <div className="editor-section-title">{group.category}</div>
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'6px'}}>
                        {group.fields.map(f => (
                            <div key={f.key}>
                                <label style={{fontSize:'10px',color:'#64748b',marginBottom:'2px',display:'block'}}>{f.label}</label>
                                <Field name={`${el}.labels.${f.key}`} className="form-control input-sm" component="input" type="text" placeholder={defaultLabels[f.key]} />
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>;
    }

    renderPaperTab() {
        const { el, theme, user, onClick } = this.props;
        const t = this.t.bind(this);

        return <div className="editor-tab-content">
            <div className="editor-section">
                <div className="editor-section-title">{t('templateEditor.paperSize')}</div>
                <label>{t('templateEditor.format')}</label>
                <Field className="form-control" name={`${el}.paperSize.format`} component="select">
                    <option value="A3">A3 (297 x 420 mm)</option>
                    <option value="A4">A4 (210 x 297 mm)</option>
                    <option value="A5">A5 (148 x 210 mm)</option>
                    <option value="Legal">Legal (215.9 x 355.6 mm)</option>
                    <option value="Letter">Letter (215.9 x 279.4 mm)</option>
                    <option value="Tabloid">Tabloid (279.4 x 431.8 mm)</option>
                    <option value="mC-Print3">mC-Print3 Roll (80 x 155 mm)</option>
                    <option value="mC-Print2">mC-Print2 Roll (57.5 x 155 mm)</option>
                </Field>
                <br/>
                <label>{t('templateEditor.orientation')}</label>
                <Field className="form-control" name={`${el}.paperSize.orientation`} component="select">
                    <option value="portrait">{t('templateEditor.portrait')}</option>
                    <option value="landscape">{t('templateEditor.landscape')}</option>
                </Field>
                <br/>
                <label>{t('templateEditor.margin')}</label>
                <Field name={`${el}.paperSize.margin`} type="text" component={InputField} className="form-control" validate={[validators.num]} />
            </div>

            <div className="editor-section">
                <div className="editor-section-title">{t('templateEditor.template')}</div>
                <label>{t('templateEditor.templateName')}</label>
                <Field className="form-control" name={`${el}.name`} component={InputField} type="text" value={`${el}.name`} defaultValue={theme.name} />
                <br/>
                <label>{t('templateEditor.systemAlias')} <small style={{color:'#616161',fontWeight:'normal'}}>({t('templateEditor.systemAliasDesc')})</small></label>
                <Field className="form-control" name={`${el}.systemName`} component="select">
                    <option value="">{t('templateEditor.systemAliasNone')}</option>
                    <option value="Invoice">Invoice</option>
                    <option value="Order">Order</option>
                    <option value="Proforma">Proforma</option>
                    <option value="Packing Slip">Packing Slip</option>
                    <option value="Receipt">Receipt</option>
                    <option value="Refund Invoice">Refund Invoice</option>
                    <option value="Storno Invoice">Storno Invoice</option>
                    <option value="Credit Note">Credit Note</option>
                    <option value="Sales Report">Sales Report</option>
                    <option value="Delivery Note">Delivery Note</option>
                    <option value="Purchase Order">Purchase Order</option>
                </Field>
                <small style={{color:'#64748b'}}>{t('templateEditor.posRouteDesc')}</small>
                <br/><br/>
                <Field name={`${el}.isPrimary`} id={`${el}.isPrimary`} onClick={() => {
                    const { fields } = this.props;
                    for (let i = 0; i < fields.length; i++) {
                        fields.get(i).isPrimary = false;
                    }
                }} component="input" type="checkbox" />
                <label htmlFor={`${el}.isPrimary`}>&nbsp;<i className="fa fa-star"></i> {t('templateEditor.defaultTemplate')}</label>
                <br/><small>{t('templateEditor.defaultTemplateDesc')}</small>
            </div>

            <div className="editor-section">
                <div className="editor-section-title">{t('templateEditor.documentType')}</div>
                <Field className="form-control" name={`${el}.documentType`} component="select">
                    <option value="">{t('templateEditor.invoiceCreditNote')}</option>
                    <option value="order">{t('templateEditor.order')}</option>
                    <option value="invoice">{t('templateEditor.invoiceOnly')}</option>
                    <option value="quote">{t('templateEditor.quoteEstimate')}</option>
                    <option value="proforma">{t('templateEditor.proformaInvoice')}</option>
                    <option value="refund">{t('templateEditor.refund')}</option>
                    <option value="storno">{t('templateEditor.stornoInvoice')}</option>
                    <option value="credit-note">{t('templateEditor.creditNoteOnly')}</option>
                    <option value="report">{t('templateEditor.reportLiquid')}</option>
                </Field>
                <br/>
                <label className="toggle-item">
                    <Field name={`${el}.ignoreRemovedItems`} component="input" type="checkbox" />
                    <span>&nbsp;{t('templateEditor.hideRemovedItems')}</span>
                </label>
                {user.hasMultipleLocations && <div style={{marginTop:'12px'}}>
                    <label>{t('templateEditor.location')}</label>
                    <Field name={`${el}.locationId`} component={ShopifyLocationsSelectField} />
                    <small>{t('templateEditor.locationEmpty')}</small>
                </div>}
            </div>

            <div className="editor-section" style={{borderTop:'1px solid #fee2e2',paddingTop:'16px'}}>
                <div className="editor-section-title" style={{color:'#991b1b'}}>Danger Zone</div>
                <p style={{fontSize:'11px',color:'#64748b',margin:'0 0 10px'}}>Permanently remove this template from your account.</p>
                <button type="button" className="btn btn-danger btn-sm" onClick={onClick}><i className="fa fa-trash"></i> Remove template</button>
            </div>
        </div>;
    }

    showSnippetOptions(fieldSuffix, snippet, e) {
        const rect = e.currentTarget.getBoundingClientRect();
        const containerRect = e.currentTarget.closest('.editor-tab-content').getBoundingClientRect();
        this.setState({
            snippetPopover: {
                fieldSuffix,
                snippet,
                top: rect.bottom - containerRect.top + 4,
                left: rect.left - containerRect.left
            }
        });
    }

    hideSnippetPopover() {
        this.setState({ snippetPopover: null });
    }

    insertSnippet(fieldSuffix, snippet) {
        const editorRef = fieldSuffix === 'bodyTemplate' ? this._bodyEditor : this._rowEditor;
        if (editorRef && editorRef.editor) {
            const editor = editorRef.editor;
            editor.session.insert(editor.getCursorPosition(), snippet);
            editor.focus();
        } else {
            const { el, dispatch } = this.props;
            const fieldName = `${el}.${fieldSuffix}`;
            const currentVal = (fieldSuffix === 'bodyTemplate' ? this.props.theme.bodyTemplate : this.props.theme.rowTemplate) || '';
            dispatch(require('redux-form').change('Profile', fieldName, currentVal + '\n' + snippet));
        }
        this.hideSnippetPopover();
    }

    copySnippet(snippet) {
        if (navigator.clipboard) {
            navigator.clipboard.writeText(snippet);
        } else {
            const ta = document.createElement('textarea');
            ta.value = snippet;
            document.body.appendChild(ta);
            ta.select();
            document.execCommand('copy');
            document.body.removeChild(ta);
        }
        this.hideSnippetPopover();
    }

    renderCodeTab() {
        const { el } = this.props;
        const t = this.t.bind(this);

        const useLiquid = isLiquidTemplate(this.props.theme.bodyTemplate);
        const bodySnippets = [
            { label: 'QR Code', icon: 'fa-qrcode', code: '<div id="qr-code" data-url="{{invoice.url}}"></div>' },
            { label: 'EPC Girocode', icon: 'fa-euro', requiredFeature: 'epcQrCodes', code: '<div id="epc-qr" data-iban="{{seller.bank.iban}}" data-bic="{{seller.bank.swift}}" data-recipient="{{seller.company}}" data-amount="{{invoice.final.price}}" data-currency="{{invoice.currency.main}}" data-reference="{{invoice.number.series}}{{invoice.number.id}}"></div>' },
            { label: 'Barcode 39', icon: 'fa-barcode', code: '<p style="font-family:\'Libre Barcode 39 Text\';font-size:48px;text-align:center;">*{{order.order_number}}*</p>' },
            { label: 'Barcode 128', icon: 'fa-barcode', code: '<p style="font-family:\'Libre Barcode 128\';font-size:48px;text-align:center;">{{order.order_number}}</p>' },
            { label: 'Paid Badge', icon: 'fa-check-circle', code: useLiquid ? '{% if invoice.isPaid %}{% if settings.showPaidBadge %}<div style="position:absolute;top:30px;right:30px;border:3px solid #22c55e;color:#22c55e;padding:6px 18px;font-size:18px;font-weight:700;letter-spacing:2px;transform:rotate(-12deg);opacity:0.8;">PAID</div>{% endif %}{% endif %}' : '{{#invoice.isPaid}}{{#settings.showPaidBadge}}<div style="position:absolute;top:30px;right:30px;border:3px solid #22c55e;color:#22c55e;padding:6px 18px;font-size:18px;font-weight:700;letter-spacing:2px;transform:rotate(-12deg);opacity:0.8;">PAID</div>{{/settings.showPaidBadge}}{{/invoice.isPaid}}' },
            { label: 'Shipping', icon: 'fa-truck', code: useLiquid ? '{% if settings.showShipping %}<div>\n  <strong>Ship To:</strong><br/>\n  {{order.shipping_address.name}}<br/>\n  {{order.shipping_address.address1}}<br/>\n  {{order.shipping_address.city}}, {{order.shipping_address.zip}}<br/>\n  {{order.shipping_address.country}}\n</div>{% endif %}' : '{{#settings.showShipping}}<div>\n  <strong>Ship To:</strong><br/>\n  {{order.shipping_address.name}}<br/>\n  {{order.shipping_address.address1}}<br/>\n  {{order.shipping_address.city}}, {{order.shipping_address.zip}}<br/>\n  {{order.shipping_address.country}}\n</div>{{/settings.showShipping}}' },
            { label: 'Bank', icon: 'fa-university', code: useLiquid ? '{% if settings.showBankDetails %}<div>\n  <strong>Bank Details:</strong><br/>\n  {{seller.bank.name}}<br/>\n  IBAN: {{seller.bank.iban}}<br/>\n  SWIFT: {{seller.bank.swift}}\n</div>{% endif %}' : '{{#settings.showBankDetails}}<div>\n  <strong>Bank Details:</strong><br/>\n  {{seller.bank.name}}<br/>\n  IBAN: {{seller.bank.iban}}<br/>\n  SWIFT: {{seller.bank.swift}}\n</div>{{/settings.showBankDetails}}' },
            { label: 'Tracking', icon: 'fa-map-marker', code: useLiquid ? '{% if settings.showTracking %}{% for f in order.fulfillments %}<div>\n  <strong>Tracking:</strong> {{f.tracking_number}}<br/>\n  <strong>Carrier:</strong> {{f.tracking_company}}\n</div>{% endfor %}{% endif %}' : '{{#settings.showTracking}}{{#order.fulfillments}}<div>\n  <strong>Tracking:</strong> {{tracking_number}}<br/>\n  <strong>Carrier:</strong> {{tracking_company}}\n</div>{{/order.fulfillments}}{{/settings.showTracking}}' },
            { label: 'Notes', icon: 'fa-sticky-note-o', code: useLiquid ? '{% if settings.showNotes %}{% if order.note %}<div style="margin-top:12px;padding:10px;background:#f8f9fa;border-left:3px solid {{seller.colorPalette.color5}};font-size:11px;">\n  <strong>Note:</strong> {{order.note}}\n</div>{% endif %}{% endif %}' : '{{#settings.showNotes}}{{#order.note}}<div style="margin-top:12px;padding:10px;background:#f8f9fa;border-left:3px solid {{seller.colorPalette.color5}};font-size:11px;">\n  <strong>Note:</strong> {{order.note}}\n</div>{{/order.note}}{{/settings.showNotes}}' },
            { label: 'Refunds', icon: 'fa-undo', code: useLiquid ? '{% if invoice.hasRefunds %}<div style="margin-top:12px;">\n  <strong>Refunded Total:</strong> {{invoice.final.refunded}} {{invoice.currency.main}}<br/>\n  {% for r in order.refunds %}<p><strong>Refunded on {{r.created_at | date: "%B %d, %Y"}}</strong> - Reason: {{r.note}}</p>\n  <table style="width:100%;font-size:10px;">\n    <tr><th>SKU</th><th>Item</th><th>Qty</th><th>Subtotal</th><th>Tax</th></tr>\n    {% for i in r.refund_line_items %}<tr>\n      <td>{{i.line_item.sku}}</td>\n      <td>{{i.line_item.title}}</td>\n      <td>{{i.quantity}}</td>\n      <td>{{i.subtotal}}</td>\n      <td>{{i.total_tax}}</td>\n    </tr>{% endfor %}\n  </table>{% endfor %}\n</div>{% endif %}' : '{{#invoice.hasRefunds}}<div style="margin-top:12px;">\n  <strong>Refunded Total:</strong> {{invoice.final.refunded}} {{invoice.currency.main}}<br/>\n  {{#order.refunds}}<p><strong>Refunded on {{#formatDate}}{{created_at}}, MMMM Do YYYY{{/formatDate}}</strong> - Reason: {{note}}</p>\n  <table style="width:100%;font-size:10px;">\n    <tr><th>SKU</th><th>Item</th><th>Qty</th><th>Subtotal</th><th>Tax</th></tr>\n    {{#refund_line_items}}<tr>\n      <td>{{line_item.sku}}</td>\n      <td>{{line_item.title}}</td>\n      <td>{{quantity}}</td>\n      <td>{{subtotal}}</td>\n      <td>{{total_tax}}</td>\n    </tr>{{/refund_line_items}}\n  </table>{{/order.refunds}}\n</div>{{/invoice.hasRefunds}}' },
            { label: 'Signatures', icon: 'fa-pencil', code: useLiquid ? '{% if settings.showSignatureLines %}<table style="width:100%;margin-top:40px;"><tr>\n  <td style="width:45%;border-top:1px solid #ccc;padding-top:5px;text-align:center;font-size:10px;">Seller Signature</td>\n  <td style="width:10%;"></td>\n  <td style="width:45%;border-top:1px solid #ccc;padding-top:5px;text-align:center;font-size:10px;">Buyer Signature</td>\n</tr></table>{% endif %}' : '{{#settings.showSignatureLines}}<table style="width:100%;margin-top:40px;"><tr>\n  <td style="width:45%;border-top:1px solid #ccc;padding-top:5px;text-align:center;font-size:10px;">Seller Signature</td>\n  <td style="width:10%;"></td>\n  <td style="width:45%;border-top:1px solid #ccc;padding-top:5px;text-align:center;font-size:10px;">Buyer Signature</td>\n</tr></table>{{/settings.showSignatureLines}}' },
            { label: 'Tag Check', icon: 'fa-tag', code: useLiquid ? '{% if customer_tags.wholesale %}<div style="background:#e0f2fe;padding:8px 12px;font-size:11px;border-radius:4px;margin:8px 0;">Wholesale Customer</div>{% endif %}' : '{{#customer_tags.wholesale}}<div style="background:#e0f2fe;padding:8px 12px;font-size:11px;border-radius:4px;margin:8px 0;">Wholesale Customer</div>{{/customer_tags.wholesale}}' },
            { label: 'Logo', icon: 'fa-image', code: '<img src="{{seller.logo}}" style="max-height:60px;" onerror="this.style.display=\'none\'" />' },
            { label: 'Terms', icon: 'fa-file-text-o', code: useLiquid ? '{% if settings.showTerms %}{% if settings.paymentTerms %}<div style="margin-top:16px;padding:12px 16px;border-left:3px solid {{seller.colorPalette.color3}};font-size:10px;color:#64748b;">{{settings.paymentTerms}}</div>{% endif %}{% endif %}' : '{{#settings.showTerms}}{{#settings.paymentTerms}}<div style="margin-top:16px;padding:12px 16px;border-left:3px solid {{seller.colorPalette.color3}};font-size:10px;color:#64748b;">{{settings.paymentTerms}}</div>{{/settings.paymentTerms}}{{/settings.showTerms}}' },
            { label: 'Date Format', icon: 'fa-calendar', code: useLiquid ? '{{order.created_at | date: "%B %d, %Y"}}' : '{{#formatDate}}{{order.created_at}}, MMMM Do YYYY{{/formatDate}}' },
            { label: 'Currency', icon: 'fa-money', code: useLiquid ? '{{invoice.final.price | currency: "en-US", invoice.currency.main}}' : '{{#formatCurrency}}{{invoice.final.price}}, en-US, {{invoice.currency.main}}{{/formatCurrency}}' },
            { label: 'B2B Company', icon: 'fa-building', code: useLiquid ? '{% if buyer.company %}<div>\n  <strong>{{buyer.company}}</strong>\n  {% if buyer.taxId %}<br/>VAT: {{buyer.taxId}}{% endif %}\n  {% if buyer.registrationNumber %}<br/>Reg: {{buyer.registrationNumber}}{% endif %}\n</div>{% endif %}' : '{{#buyer.company}}<div>\n  <strong>{{buyer.company}}</strong>\n  {{#buyer.taxId}}<br/>VAT: {{buyer.taxId}}{{/buyer.taxId}}\n  {{#buyer.registrationNumber}}<br/>Reg: {{buyer.registrationNumber}}{{/buyer.registrationNumber}}\n</div>{{/buyer.company}}' },
            { label: 'Discount Total', icon: 'fa-tags', code: useLiquid ? '{% if total.hasDiscount %}<table style="width:100%;font-size:12px;"><tr>\n  <td class="text-right"><strong>Subtotal</strong></td>\n  <td class="text-right"><strong>{{total.subtotal}} {{invoice.currency.main}}</strong></td>\n</tr><tr>\n  <td class="text-right" style="color:#dc2626;">Discount</td>\n  <td class="text-right" style="color:#dc2626;">−{{total.discount}} {{invoice.currency.main}}</td>\n</tr></table>{% endif %}' : '{{#total.hasDiscount}}<table style="width:100%;font-size:12px;"><tr>\n  <td class="text-right"><strong>Subtotal</strong></td>\n  <td class="text-right"><strong>{{total.subtotal}} {{invoice.currency.main}}</strong></td>\n</tr><tr>\n  <td class="text-right" style="color:#dc2626;">Discount</td>\n  <td class="text-right" style="color:#dc2626;">−{{total.discount}} {{invoice.currency.main}}</td>\n</tr></table>{{/total.hasDiscount}}' },
            { label: 'Order Metafield', icon: 'fa-database', code: '{{order.meta.custom.YOUR_FIELD}}' },
        ];

        const rowSnippets = [
            { label: 'SKU', icon: 'fa-tag', code: '{{sku}}' },
            { label: 'Weight', icon: 'fa-balance-scale', code: '{{item.weight}}' },
            { label: 'Barcode', icon: 'fa-barcode', code: '<p style="font-family:\'Libre Barcode 128\';font-size:32px;margin:0;">{{sku}}</p>' },
            { label: 'HS Code', icon: 'fa-globe', code: '{{item.harmonized_system_code}}' },
            { label: 'Image', icon: 'fa-image', code: '<img src="{{image.src}}" style="max-width:40px;max-height:40px;" />' },
            { label: 'Tax Rate', icon: 'fa-percent', code: '{{taxRate}}%' },
            { label: 'Discount', icon: 'fa-minus-circle', code: '{{item.total_discount}}' },
            { label: 'Line Discount', icon: 'fa-minus-circle', code: useLiquid ? '{% if hasDiscount %}<span style="color:#dc2626;">−{{discount}} {{invoice.currency.main}}</span>{% endif %}' : '{{#hasDiscount}}<span style="color:#dc2626;">−{{discount}} {{invoice.currency.main}}</span>{{/hasDiscount}}' },
            { label: 'Metafield', icon: 'fa-database', code: '{{item.variant.meta.YOUR_FIELD}}' },
        ];

        const { snippetPopover } = this.state;

        return <div className="editor-tab-content" style={{position:'relative'}} onClick={(e) => { if (!e.target.closest('.snippet-popover') && !e.target.closest('.snippet-btn')) this.hideSnippetPopover(); }}>
            <style dangerouslySetInnerHTML={{__html: `
                .snippet-btns { display: flex; flex-wrap: wrap; gap: 4px; margin-bottom: 8px; }
                .snippet-btn { display: inline-flex; align-items: center; gap: 4px; padding: 3px 8px; border: 1px solid #e2e8f0; border-radius: 4px; font-size: 10px; cursor: pointer; background: #fff; color: #475569; transition: all 0.12s; }
                .snippet-btn:hover { border-color: #6366f1; color: #6366f1; background: #f5f3ff; }
                .snippet-btn-locked { opacity: 0.55; border-style: dashed; }
                .snippet-btn-locked:hover { border-color: #94a3b8; color: #64748b; background: #f8fafc; }
                .snippet-btn .fa { font-size: 10px; }
                .snippet-popover { position: absolute; z-index: 1000; background: #fff; border: 1px solid #e2e8f0; border-radius: 6px; box-shadow: 0 4px 12px rgba(0,0,0,0.12); padding: 4px; display: flex; gap: 2px; }
                .snippet-popover-btn { display: flex; align-items: center; gap: 5px; padding: 6px 12px; border: none; background: none; font-size: 11px; cursor: pointer; border-radius: 4px; color: #334155; white-space: nowrap; }
                .snippet-popover-btn:hover { background: #f1f5f9; }
                .snippet-popover-btn .fa { font-size: 11px; color: #64748b; }
            `}}></style>

            {snippetPopover && <div className="snippet-popover" style={{top: snippetPopover.top, left: snippetPopover.left}}>
                <button type="button" className="snippet-popover-btn" onClick={() => this.insertSnippet(snippetPopover.fieldSuffix, snippetPopover.snippet)}>
                    <i className="fa fa-plus-circle"></i> {this.t('templateEditor.insert')}
                </button>
                <button type="button" className="snippet-popover-btn" onClick={() => this.copySnippet(snippetPopover.snippet)}>
                    <i className="fa fa-clipboard"></i> {this.t('templateEditor.copy')}
                </button>
            </div>}

            <div className="editor-section">
                <div className="editor-section-title">{this.t('templateEditor.bodyTemplate')}</div>
                <p style={{fontSize:'11px', color:'#666', margin:'0 0 6px'}}>
                    {this.t('templateEditor.bodyTemplateDesc')}
                </p>
                <div className="snippet-btns">
                    {bodySnippets.map(s => {
                        var sub = this.props.user && this.props.user.profile ? this.props.user.profile.subscription : 'free';
                        var locked = s.requiredFeature && !canUseFeature(sub, s.requiredFeature);
                        return <span key={s.label} className={locked ? "snippet-btn snippet-btn-locked" : "snippet-btn"} onClick={(e) => locked ? this.setState({ copilotMessages: (this.state.copilotMessages || []).concat([{ role: 'assistant', content: s.label + ' requires a Starter or Professional plan. [Upgrade](/billing) to unlock.' }]) }) : this.showSnippetOptions('bodyTemplate', s.code, e)} title={locked ? s.label + ' (requires paid plan)' : s.code}>
                            <i className={`fa ${locked ? 'fa-lock' : s.icon}`}></i> {s.label}
                        </span>;
                    })}
                </div>
                <Field rows="30" style={{ width: "100%", fontSize: '12px' }}
                    name={`${el}.bodyTemplate`} id={`${el}.bodyTemplate`} component={CodeEditor} validate={[validators.html]} editorRef={(ref) => { this._bodyEditor = ref; }} />
            </div>
            <div className="editor-section">
                <div className="editor-section-title">{this.t('templateEditor.rowTemplate')}</div>
                <p style={{fontSize:'11px', color:'#666', margin:'0 0 6px'}}>
                    {this.t('templateEditor.rowTemplateDesc')}
                </p>
                <div className="snippet-btns">
                    {rowSnippets.map(s => (
                        <span key={s.label} className="snippet-btn" onClick={(e) => this.showSnippetOptions('rowTemplate', s.code, e)} title={s.code}>
                            <i className={`fa ${s.icon}`}></i> {s.label}
                        </span>
                    ))}
                </div>
                <Field rows="10" style={{ width: "100%", fontSize: '12px' }}
                    name={`${el}.rowTemplate`} id={`${el}.rowTemplate`} component={CodeEditor} editorRef={(ref) => { this._rowEditor = ref; }} />
            </div>
            <CollapsablePanel location="default" section="template-reference" title={this.t('templateEditor.variableReference')}>
                <div className="template-reference" dangerouslySetInnerHTML={{__html: `
                    <h5>Invoice</h5>
                    <code>{{invoice.number.series}}{{invoice.number.id}}</code> - Invoice number<br/>
                    <code>{{invoice.date}}</code> - Formatted invoice date<br/>
                    <code>{{invoice.dueDate}}</code> - Formatted due date<br/>
                    <code>{{invoice.currency.main}}</code> - Currency code (e.g. USD, EUR)<br/>
                    <code>{{invoice.final.price}}</code> - Total amount incl. tax<br/>
                    <code>{{invoice.final.discount}}</code> - Discount amount<br/>
                    <code>{{invoice.taxes.vta}}</code> - Total tax amount<br/>
                    <code>{{#invoice.isPaid}}...{{/invoice.isPaid}}</code> - Show only if paid<br/>
                    <code>{{#invoice.isCreditNote}}...{{/invoice.isCreditNote}}</code> - Credit note conditional<br/>
                    <code>{{#invoice.isStorno}}...{{/invoice.isStorno}}</code> - Storno conditional<br/>
                    <code>{{invoice.parent}}</code> - Parent invoice reference<br/>
                    <code>{{invoice.paymentMethod}}</code> - Payment gateway<br/>
                    <h5>Order (Shopify)</h5>
                    <code>{{order.order_number}}</code> - Order number (e.g. 1001)<br/>
                    <code>{{order.name}}</code> - Order name (e.g. #1001)<br/>
                    <code>{{order.created_at}}</code> - Order creation date (raw ISO)<br/>
                    <code>{{order.note}}</code> - Customer order notes<br/>
                    <code>{{order.tags}}</code> - Order tags (comma separated)<br/>
                    <code>{{order.financial_status}}</code> - paid, pending, refunded...<br/>
                    <code>{{order.total_price}}</code> - Order total (Shopify)<br/>
                    <code>{{order.subtotal_price}}</code> - Subtotal (Shopify)<br/>
                    <code>{{order.total_tax}}</code> - Tax total (Shopify)<br/>
                    <code>{{order.total_discounts}}</code> - Discounts (Shopify)<br/>
                    <code>{{order.shipping_address.name}}</code> - Ship to name<br/>
                    <code>{{order.shipping_address.address1}}</code> - Ship to address<br/>
                    <code>{{order.shipping_address.city}}</code>, <code>{{order.shipping_address.zip}}</code>, <code>{{order.shipping_address.country}}</code><br/>
                    <code>{{order.billing_address.name}}</code> - Bill to name<br/>
                    <code>{{order.billing_address.phone}}</code> - Bill to phone<br/>
                    <code>{{order.customer.email}}</code> - Customer email<br/>
                    <code>{{#order.fulfillments}}{{tracking_number}} {{tracking_company}}{{/order.fulfillments}}</code><br/>
                    <code>{{#order.transactions}}{{gateway}} {{amount}}{{/order.transactions}}</code><br/>
                    <h5>Seller (Your Business)</h5>
                    <code>{{seller.company}}</code> - Company name<br/>
                    <code>{{seller.registrationNumber}}</code> - Registration / CUI<br/>
                    <code>{{seller.taxId}}</code> - Tax ID / VAT number<br/>
                    <code>{{seller.email}}</code>, <code>{{seller.phone}}</code>, <code>{{seller.website}}</code><br/>
                    <code>{{seller.address.street}}</code>, <code>{{seller.address.city}}</code>, <code>{{seller.address.zip}}</code>, <code>{{seller.address.country}}</code><br/>
                    <code>{{seller.bank.name}}</code> - Bank name<br/>
                    <code>{{seller.bank.iban}}</code> - IBAN<br/>
                    <code>{{seller.bank.swift}}</code> - SWIFT/BIC<br/>
                    <code>{{seller.bank.currency}}</code> - Bank currency<br/>
                    <code>{{seller.logo}}</code> - Logo URL<br/>
                    <code>{{seller.colorPalette.color1}}</code> - Text color<br/>
                    <code>{{seller.colorPalette.color2}}</code> - Background color<br/>
                    <code>{{seller.colorPalette.color3}}</code> - Border color<br/>
                    <code>{{seller.colorPalette.color4}}</code> - Primary / heading color<br/>
                    <code>{{seller.colorPalette.color5}}</code> - Accent color<br/>
                    <h5>Buyer (Customer)</h5>
                    <code>{{buyer.name}}</code> - Customer name<br/>
                    <code>{{buyer.company}}</code> - Company name (B2B)<br/>
                    <code>{{buyer.taxId}}</code> - Customer tax ID<br/>
                    <code>{{buyer.registrationNumber}}</code> - Customer reg. number<br/>
                    <code>{{buyer.email}}</code>, <code>{{buyer.phone}}</code><br/>
                    <code>{{buyer.address.street}}</code>, <code>{{buyer.address.city}}</code>, <code>{{buyer.address.zip}}</code>, <code>{{buyer.address.country}}</code><br/>
                    <code>{{#buyer.notCompany}}...{{/buyer.notCompany}}</code> - Individual customer<br/>
                    <code>{{^buyer.notCompany}}...{{/buyer.notCompany}}</code> - Company / B2B<br/>
                    <h5>Settings Toggles (use in Body Template)</h5>
                    <code>{{#settings.showShipping}}...{{/settings.showShipping}}</code> - Shipping section<br/>
                    <code>{{#settings.showBankDetails}}...{{/settings.showBankDetails}}</code> - Bank details<br/>
                    <code>{{#settings.showNotes}}...{{/settings.showNotes}}</code> - Order notes<br/>
                    <code>{{#settings.showTracking}}...{{/settings.showTracking}}</code> - Tracking info<br/>
                    <code>{{#settings.showTerms}}...{{/settings.showTerms}}</code> - Terms section<br/>
                    <code>{{#settings.showPaidBadge}}...{{/settings.showPaidBadge}}</code> - Paid badge<br/>
                    <code>{{#settings.showSignatureLines}}...{{/settings.showSignatureLines}}</code> - Signatures<br/>
                    <code>{{#settings.showDueDate}}...{{/settings.showDueDate}}</code> - Due date<br/>
                    <code>{{settings.invoiceTitle}}</code> - Custom document title<br/>
                    <code>{{settings.thankYouText}}</code> - Thank you message<br/>
                    <code>{{settings.paymentTerms}}</code> - Payment terms text<br/>
                    <code>{{settings.font}}</code> - Selected font family<br/>
                    <h5>Row Template Variables (line items)</h5>
                    <code>{{nrCrt}}</code> - Row number (1, 2, 3...)<br/>
                    <code>{{description}}</code> - Item description / title<br/>
                    <code>{{quantity}}</code> - Quantity<br/>
                    <code>{{sku}}</code> - Product SKU<br/>
                    <code>{{unitPrice.main}}</code> - Unit price (net)<br/>
                    <code>{{unitPrice.gross}}</code> - Unit price (incl. tax)<br/>
                    <code>{{amount.main}}</code> - Line total (net)<br/>
                    <code>{{amount.gross}}</code> - Line total (incl. tax)<br/>
                    <code>{{taxRate}}</code> - Tax rate %<br/>
                    <code>{{taxPrice}}</code> - Tax amount for line<br/>
                    <code>{{item.sku}}</code> - Shopify SKU<br/>
                    <code>{{item.weight}}</code> - Item weight<br/>
                    <code>{{invoice.currency.main}}</code> - Currency code (available in row template)<br/>
                    <code>{{invoice.currency.secondary}}</code> - Secondary currency code<br/>
                    <h5>Helpers</h5>
                    <code>{{#formatDate}}{{order.created_at}}, MMM Do YY{{/formatDate}}</code> - Format any date<br/>
                    <code>{{#formatCurrency}}{{invoice.final.price}}, en-US, USD{{/formatCurrency}}</code> - Format currency<br/>
                    <code>{{#formatCurrency}}{{amount}}, en-US, USD, 1.2{{/formatCurrency}}</code> - With conversion rate<br/>
                    <code>{{{description_rows}}}</code> - Rendered line items (in body template)<br/>
                    <h5>Conditionals / Tags</h5>
                    <code>{{#customer_tags.wholesale}}...{{/customer_tags.wholesale}}</code> - Customer tag check<br/>
                    <code>{{#order_tags.vip}}...{{/order_tags.vip}}</code> - Order tag check<br/>
                    <code>{{#showVTA}}...{{/showVTA}}</code> - Show if tax is enabled<br/>
                    <h5>Special Elements</h5>
                    <code>&lt;div id="qr-code" data-url="{{invoice.url}}"&gt;&lt;/div&gt;</code> - QR Code (scannable link to invoice)<br/>
                    <code>&lt;div id="epc-qr" data-iban="{{seller.bank.iban}}" data-bic="{{seller.bank.swift}}" data-recipient="{{seller.company}}" data-amount="{{invoice.final.price}}" data-currency="{{invoice.currency.main}}" data-reference="{{invoice.number.series}}{{invoice.number.id}}"&gt;&lt;/div&gt;</code> - EPC Girocode (SEPA payment QR)<br/>
                    <code>&lt;p style="font-family:'Libre Barcode 39 Text';font-size:48px;"&gt;*{{order.order_number}}*&lt;/p&gt;</code> - Barcode 39<br/>
                    <code>&lt;p style="font-family:'Libre Barcode 128';font-size:48px;"&gt;{{order.order_number}}&lt;/p&gt;</code> - Barcode 128<br/>
                    <code>&lt;img src="{{seller.logo}}" style="max-height:60px;" onerror="this.style.display='none'" /&gt;</code> - Logo (with fallback)<br/>
                    <h5>Metafields (Line Items)</h5>
                    <code>{{item.variant.meta.FIELD_NAME}}</code> - Variant metafield (dots replaced by _)<br/>
                    <code>{{item.harmonized_system_code}}</code> - HS code for customs<br/>
                    <code>&lt;p style="font-family:'Libre Barcode 128';font-size:32px;"&gt;{{sku}}&lt;/p&gt;</code> - SKU as barcode (rendered via font)<br/>
                `}}></div>
            </CollapsablePanel>
        </div>;
    }

    initCopilotEngine(forceReload) {
        if (!forceReload && (this.copilotWorker && this.state.copilotEngineReady)) return;

        if (typeof navigator === 'undefined' || !navigator.gpu) {
            this.setState({
                copilotError: 'WebGPU not supported. Use Chrome 113+ or Edge 113+.',
                copilotEngineLoading: false,
            });
            return;
        }

        this.setState({ copilotEngineLoading: true, copilotEngineReady: false, copilotError: null });

        if (this.copilotWorker && forceReload) {
            this.copilotWorker.terminate();
            this.copilotWorker = null;
        }

        if (!this.copilotWorker) {
            try {
                this.copilotWorker = new Worker('/js/copilot-worker.js', { type: 'module' });
            } catch (e) {
                this.setState({
                    copilotError: 'Failed to create worker: ' + e.message,
                    copilotEngineLoading: false,
                });
                return;
            }

            this.copilotWorker.onmessage = function(e) {
                if (e.data.type === 'progress') {
                    this._copilotProgressText = e.data.text || '';
                    var el = document.getElementById('copilot-progress-text');
                    var bar = document.getElementById('copilot-progress-fill');
                    if (el) el.textContent = this._copilotProgressText;
                    if (bar) {
                        var pct = 0;
                        var m = this._copilotProgressText.match(/(\d+)%/);
                        if (m) pct = parseInt(m[1], 10);
                        bar.style.width = pct + '%';
                    }
                } else if (e.data.type === 'ready') {
                    this.setState({
                        copilotEngineReady: true,
                        copilotEngineLoading: false,
                    });
                } else if (e.data.type === 'generating') {
                    this.setState({ copilotLoading: true });
                } else if (e.data.type === 'result') {
                    this.handleCopilotResult(e.data.content);
                } else if (e.data.type === 'error') {
                    this.setState({
                        copilotError: e.data.text,
                        copilotLoading: false,
                        copilotEngineLoading: false,
                    });
                }
            }.bind(this);

            this.copilotWorker.onerror = function(e) {
                this.setState({
                    copilotError: 'Worker error: ' + (e.message || 'Failed to load. Ensure you use Chrome 113+.'),
                    copilotEngineLoading: false,
                });
            }.bind(this);
        }

        this.copilotWorker.postMessage({ type: 'init', model: this.state.copilotModel });
        this.initEmbeddingsWorker();
    }

    initEmbeddingsWorker() {
        if (this.embeddingsWorker || this.embeddingsReady) return;
        try {
            this.embeddingsWorker = new Worker('/js/embeddings-worker.js', { type: 'module' });
            this.embeddingsWorker.onmessage = function(e) {
                if (e.data.type === 'ready') {
                    var texts = RAG_EXAMPLES.map(function(ex) { return ex.query; });
                    this.embeddingsWorker.postMessage({ type: 'index', texts: texts });
                } else if (e.data.type === 'indexed') {
                    this.embeddingsReady = true;
                } else if (e.data.type === 'results') {
                    if (this._embeddingsResolve) {
                        this._embeddingsResolve(e.data.matches);
                        this._embeddingsResolve = null;
                    }
                }
            }.bind(this);
            this.embeddingsWorker.postMessage({ type: 'init' });
        } catch (e) {}
    }

    semanticSearch(query) {
        if (!this.embeddingsReady || !this.embeddingsWorker) return Promise.resolve(null);
        var self = this;
        return new Promise(function(resolve) {
            self._embeddingsResolve = resolve;
            self.embeddingsWorker.postMessage({ type: 'search', query: query, topK: 3 });
            setTimeout(function() { if (self._embeddingsResolve) { self._embeddingsResolve(null); self._embeddingsResolve = null; } }, 3000);
        });
    }

    sendCopilotMessage() {
        var input = this.state.copilotInput;
        if (!input.trim()) return;

        var messages = this.state.copilotMessages;
        var lastAction = null;
        for (var i = messages.length - 1; i >= 0; i--) {
            if (messages[i].action && messages[i].action !== 'undo' && messages[i].action !== 'redo') {
                lastAction = messages[i].action;
                break;
            }
        }

        var classified = classifyIntent(input.trim(), lastAction);

        this.setState({
            copilotInput: '',
            copilotProposal: null,
            copilotMessages: this.state.copilotMessages.concat([{ role: 'user', content: input.trim() }]),
        });

        if (classified.intent === 'llm_fallback') {
            if (!this.state.copilotEngineReady && !this.state.copilotEngineLoading) {
                this.setState({ copilotPendingLLMInput: input.trim(), copilotShowModelConfirm: true });
                return;
            }
            this.sendToLLM(input.trim());
        } else {
            this.executeDeterministicAction(classified, input.trim());
        }
    }

    executeDeterministicAction(action, originalInput) {
        if (this.copilotHistory.length === 0) {
            this.saveCopilotSnapshot();
        }
        var dispatch = this.props.dispatch;
        var el = this.props.el;
        var theme = this.props.theme;
        var subscription = this.props.user && this.props.user.profile ? this.props.user.profile.subscription : 'free';

        if (action.intent === 'translate') {
            this.applyLanguagePreset(action.langCode);
            var preset = LANGUAGE_PRESETS.find(function(p) { return p.code === action.langCode; });
            var response = preset
                ? 'Translated all labels to ' + action.langName + '. Applied ' + Object.keys(preset.labels).length + ' label translations.'
                : 'Language "' + action.langName + '" not found in presets.';
            this.setState({ copilotMessages: this.state.copilotMessages.concat([{ role: 'assistant', content: response, action: 'translate' }]) });
            setTimeout(function() { this.saveCopilotSnapshot(); }.bind(this), 0);
            return;
        }

        var result = execAction(action, theme, subscription);

        if (result.gated) {
            this.setState({ copilotMessages: this.state.copilotMessages.concat([{ role: 'assistant', content: result.response }]), copilotLoading: false });
            return;
        }

        result.changes.forEach(function(c) {
            dispatch(change("Profile", el + "." + c.path, c.value));
        });

        this.setState({
            copilotMessages: this.state.copilotMessages.concat([{
                role: 'assistant',
                content: result.response,
                action: action.intent,
            }]),
        });
        setTimeout(function() { this.saveCopilotSnapshot(); }.bind(this), 0);
    }

    sendToLLM(input) {
        if (!this.copilotWorker) {
            this.setState({
                copilotMessages: this.state.copilotMessages.concat([{
                    role: 'assistant',
                    content: 'This request requires the AI model for structural template edits. Please load the AI engine first.',
                    needsEngine: true,
                }]),
            });
            return;
        }

        var theme = this.props.theme;
        this.setState({ copilotLoading: true });

        var self = this;
        this.semanticSearch(input).then(function(matches) {
            var messages = buildLLMMessages(input, theme.bodyTemplate || '', theme.rowTemplate || '', matches);
            self.copilotWorker.postMessage({ type: 'generate', messages: messages });
        });
    }

    abortCopilot() {
        if (this.copilotWorker) {
            this.copilotWorker.postMessage({ type: 'abort' });
            this.setState({ copilotLoading: false });
        }
    }

    saveCopilotSnapshot() {
        var snapshot = createSnapshot(this.props.theme);
        this.copilotHistory = this.copilotHistory.slice(0, this.copilotHistoryIndex + 1);
        this.copilotHistory.push(snapshot);
        this.copilotHistoryIndex = this.copilotHistory.length - 1;
        this.setState({ copilotCanUndo: this.copilotHistoryIndex > 0, copilotCanRedo: false });
    }

    copilotUndo() {
        if (this.copilotHistoryIndex <= 0) return;
        this.copilotHistoryIndex--;
        this.restoreCopilotSnapshot(this.copilotHistory[this.copilotHistoryIndex]);
        this.setState({
            copilotMessages: this.state.copilotMessages.concat([{
                role: 'assistant', content: 'Undid last action.', action: 'undo',
            }]),
            copilotCanUndo: this.copilotHistoryIndex > 0,
            copilotCanRedo: true,
        });
    }

    copilotRedo() {
        if (this.copilotHistoryIndex >= this.copilotHistory.length - 1) return;
        this.copilotHistoryIndex++;
        this.restoreCopilotSnapshot(this.copilotHistory[this.copilotHistoryIndex]);
        this.setState({
            copilotMessages: this.state.copilotMessages.concat([{
                role: 'assistant', content: 'Redid action.', action: 'redo',
            }]),
            copilotCanUndo: true,
            copilotCanRedo: this.copilotHistoryIndex < this.copilotHistory.length - 1,
        });
    }

    restoreCopilotSnapshot(compressed) {
        var snapshot = decompressSnapshot(compressed);
        var dispatch = this.props.dispatch;
        var el = this.props.el;
        dispatch(change("Profile", el + ".bodyTemplate", snapshot.bodyTemplate));
        dispatch(change("Profile", el + ".rowTemplate", snapshot.rowTemplate));
        Object.keys(snapshot.labels).forEach(function(key) {
            dispatch(change("Profile", el + ".labels." + key, snapshot.labels[key]));
        });
        Object.keys(snapshot.settings).forEach(function(key) {
            dispatch(change("Profile", el + ".settings." + key, snapshot.settings[key]));
        });
        Object.keys(snapshot.colorPalette).forEach(function(key) {
            dispatch(change("Profile", el + ".colorPalette." + key, snapshot.colorPalette[key]));
        });
        Object.keys(snapshot.paperSize).forEach(function(key) {
            dispatch(change("Profile", el + ".paperSize." + key, snapshot.paperSize[key]));
        });
    }

    handleCopilotResult(content) {
        var proposal = parseLLMResponse(content);

        var failMsg = 'Could not generate valid edits. Try rephrasing your request.';
        if (!proposal) {
            failMsg += ' You can also try the larger Qwen3 8B model in the dropdown below for better results on complex requests.';
        }

        this.setState({
            copilotLoading: false,
            copilotProposal: proposal,
            copilotMessages: this.state.copilotMessages.concat([{
                role: 'assistant',
                content: proposal ? (proposal.explanation || 'Changes proposed.') + ' Review the proposed edit below, then click Apply Changes.' : failMsg,
            }]),
        });
    }

    applyCopilotProposal() {
        var proposal = this.state.copilotProposal;
        if (!proposal) return;
        if (this.copilotHistory.length === 0) {
            this.saveCopilotSnapshot();
        }
        var dispatch = this.props.dispatch;
        var el = this.props.el;
        var theme = this.props.theme;

        var result = applyProposal(proposal, theme.bodyTemplate || '', theme.rowTemplate || '');
        if (result.bodyChanged) dispatch(change("Profile", el + ".bodyTemplate", result.body));
        if (result.rowChanged) dispatch(change("Profile", el + ".rowTemplate", result.row));

        var msg = null;
        if (result.failedEdits && result.failedEdits.length > 0) {
            msg = 'Applied changes. ' + result.failedEdits.length + ' edit(s) could not find exact match in template — content was appended to the end. You may want to reposition it in the Code tab.';
        }

        this.setState({
            copilotProposal: null,
            copilotMessages: msg ? this.state.copilotMessages.concat([{ role: 'assistant', content: msg }]) : this.state.copilotMessages,
        });
        setTimeout(function() { this.saveCopilotSnapshot(); }.bind(this), 0);
    }

    getCopilotProgressPercent() {
        var text = this._copilotProgressText || '';
        if (!text) return 0;
        var match = text.match(/(\d+)%/);
        if (match) return parseInt(match[1], 10);
        if (text.indexOf('Creating') !== -1) return 2;
        if (text.indexOf('Loading') !== -1) return 5;
        if (text.indexOf('Downloading') !== -1 || text.indexOf('Fetching') !== -1) return 10;
        if (text.indexOf('Initializing') !== -1) return 90;
        return 0;
    }

    renderCopilotTab() {
        const t = this.t.bind(this);
        var state = this.state;
        var engineReady = state.copilotEngineReady;
        var engineLoading = state.copilotEngineLoading;
        var loading = state.copilotLoading;
        var error = state.copilotError;
        var proposal = state.copilotProposal;
        var messages = state.copilotMessages;
        var input = state.copilotInput;
        var lowMemory = typeof navigator !== 'undefined' && navigator.deviceMemory && navigator.deviceMemory < 8;

        return <div className="editor-tab-content">
            <style dangerouslySetInnerHTML={{__html: `
                .copilot-section { margin-bottom: 16px; }
                .copilot-progress-bar { height: 4px; background: #e5e7eb; border-radius: 2px; overflow: hidden; margin: 10px 0; }
                .copilot-progress-fill { height: 100%; background: #303030; border-radius: 2px; transition: width 0.3s ease; }
                .copilot-messages { max-height: 280px; overflow-y: auto; margin-bottom: 12px; padding: 8px 12px; background: #f7f7f7; border-radius: 8px; border: 1px solid #e3e3e3; }
                .copilot-msg { margin-bottom: 8px; font-size: 13px; line-height: 20px; color: #303030; }
                .copilot-msg-user { color: #303030; }
                .copilot-msg-user:before { content: "You: "; font-weight: 600; }
                .copilot-msg-ai { color: #303030; }
                .copilot-msg-ai:before { content: "AI: "; font-weight: 600; }
                .copilot-msg-action { display: inline-block; margin-left: 6px; padding: 2px 8px; background: #eaf4ff; color: #003a5a; border-radius: 9999px; font-size: 12px; font-weight: 500; line-height: 16px; vertical-align: middle; }
                .copilot-input-row { display: flex; gap: 8px; align-items: flex-start; }
                .copilot-input { flex: 1; background: #fdfdfd; color: #303030; border: 1px solid #8a8a8a; border-radius: 8px; padding: 6px 12px; font-size: 13px; resize: none; outline: none; font-family: inherit; line-height: 20px; transition: border-color 0.15s; }
                .copilot-input:hover { border-color: #616161; }
                .copilot-input:focus { border-color: #005bd3; outline: none; box-shadow: inset 0 0 0 1px #005bd3; }
                .copilot-input:disabled { background: #f7f7f7; border-color: #e3e3e3; color: #8a8a8a; }
                .copilot-send { min-height: 32px; padding: 6px 12px; background: #303030; color: #fff; border: none; border-radius: 8px; font-size: 13px; line-height: 20px; cursor: pointer; font-weight: 600; box-shadow: inset 0 1px 0 rgba(255,255,255,0.16), 0 1px 0 rgba(0,0,0,0.1); transition: background-color 0.15s; }
                .copilot-send:not(:disabled):hover { background: #1a1a1a; }
                .copilot-send:disabled { background: #f2f2f2; color: #8a8a8a; box-shadow: none; cursor: default; }
                .copilot-proposal { background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 6px; padding: 12px; margin-top: 12px; }
                .copilot-proposal-title { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; color: #166534; margin-bottom: 8px; }
                .copilot-proposal-code { max-height: 200px; overflow: auto; background: #1e293b; color: #e2e8f0; padding: 10px; border-radius: 4px; font-size: 11px; font-family: 'JetBrains Mono', monospace; white-space: pre-wrap; word-break: break-all; }
                .copilot-actions { display: flex; gap: 8px; margin-top: 10px; }
                .copilot-apply { min-height: 32px; padding: 6px 12px; background: #303030; color: #fff; border: none; border-radius: 8px; font-size: 13px; line-height: 20px; cursor: pointer; font-weight: 600; box-shadow: inset 0 1px 0 rgba(255,255,255,0.16), 0 1px 0 rgba(0,0,0,0.1); transition: background-color 0.15s; }
                .copilot-apply:hover { background: #1a1a1a; }
                .copilot-reject { min-height: 32px; padding: 5px 12px; background: #fff; color: #303030; border: 1px solid #e3e3e3; border-radius: 8px; box-shadow: 0 1px 0 rgba(26,26,26,0.07); font-size: 13px; line-height: 20px; font-weight: 600; cursor: pointer; transition: background-color 0.15s; }
                .copilot-reject:disabled { background: #f2f2f2; border-color: #f2f2f2; color: #8a8a8a; box-shadow: none; cursor: default; }
                .copilot-reject:not(:disabled):hover { background: #fafafa; }
                .copilot-send:focus-visible, .copilot-apply:focus-visible, .copilot-reject:focus-visible { outline: 2px solid #005bd3; outline-offset: 1px; }
                .copilot-spinner { display: inline-block; width: 14px; height: 14px; border: 2px solid #e5e7eb; border-top-color: #303030; border-radius: 50%; animation: spin 0.7s linear infinite; margin-right: 6px; vertical-align: middle; }
                @keyframes spin { to { transform: rotate(360deg); } }
                .copilot-loading-text { font-size: 13px; color: #616161; display: flex; align-items: center; padding: 8px 0; }
                .copilot-engine-bar { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; padding: 8px 12px; background: #f7f7f7; border-radius: 8px; margin-bottom: 12px; font-size: 12px; line-height: 16px; color: #616161; }
                .copilot-model-select { margin-left: auto; height: 32px; font-size: 13px; padding: 0 8px; border: 1px solid #8a8a8a; border-radius: 8px; background: #fdfdfd; color: #303030; cursor: pointer; outline: none; box-shadow: none; }
                .copilot-model-select:hover { border-color: #616161; }
                .copilot-model-select:focus { outline: none; box-shadow: none; border-color: #8a8a8a; }
                .copilot-model-select:focus-visible { outline: none; border-color: #005bd3; box-shadow: inset 0 0 0 1px #005bd3; }
                .copilot-model-select:disabled { background: #f7f7f7; border-color: #e3e3e3; color: #8a8a8a; cursor: default; }
                .copilot-engine-dot { width: 6px; height: 6px; border-radius: 50%; }
            `}}></style>

            {state.copilotShowModelConfirm && !error && <div style={{background:'#fff1e3', border:'1px solid #ffd6a4', padding:'12px', borderRadius:'12px', fontSize:'13px', color:'#5e4200', marginBottom:'12px'}}>
                <i className="fa fa-exclamation-circle" style={{marginRight:'6px'}}></i>
                <strong>{this.t('templateEditor.loadAiQuestion')}</strong>
                <p style={{margin:'8px 0', color:'#5e4200', lineHeight:'20px'}}>
                    {this.t('templateEditor.loadAiDesc')}
                </p>
                {state.copilotModel === 'Qwen3-8B-q4f16_1-MLC' && <p style={{margin:'8px 0', color:'#8e0b21', lineHeight:'20px'}}>
                    <i className="fa fa-exclamation-triangle" style={{marginRight:'4px'}}></i>
                    First load downloads ~5.7 GB and needs ~6 GB of free GPU memory (about 8 GB RAM). If your device has less memory, pick the smaller Qwen3 4B or Qwen 2.5 1.5B from the model dropdown instead.
                </p>}
                <div style={{display:'flex', gap:'8px'}}>
                    <button type="button" className="copilot-apply"
                        onClick={function() {
                            this.setState({ copilotShowModelConfirm: false });
                            this.initCopilotEngine();
                            if (this.state.copilotPendingLLMInput) {
                                var pending = this.state.copilotPendingLLMInput;
                                this.setState({ copilotPendingLLMInput: null });
                                var checkReady = setInterval(function() {
                                    if (this.state.copilotEngineReady) {
                                        clearInterval(checkReady);
                                        this.sendToLLM(pending);
                                    } else if (this.state.copilotError) {
                                        clearInterval(checkReady);
                                    }
                                }.bind(this), 500);
                            }
                        }.bind(this)}
                    >{this.t('templateEditor.loadModelContinue')}</button>
                    <button type="button" className="copilot-reject"
                        onClick={function() { this.setState({ copilotShowModelConfirm: false, copilotPendingLLMInput: null }); }.bind(this)}
                    >{this.t('templateEditor.cancel')}</button>
                </div>
            </div>}

            {error && <div style={{background:'#fee8eb', border:'1px solid #fec1c7', color:'#8e0b21', padding:'8px 12px', borderRadius:'8px', fontSize:'13px', marginBottom:'12px'}}>
                <i className="fa fa-exclamation-triangle" style={{marginRight:'6px'}}></i>{error}
                <button type="button" style={{float:'right', background:'none', border:'none', color:'#8e0b21', cursor:'pointer', fontSize:'16px', lineHeight:'20px'}} onClick={function() { this.setState({ copilotError: null }); }.bind(this)}>&times;</button>
            </div>}

            {messages.length > 0 && <div className="copilot-messages">
                {messages.map(function(msg, i) {
                    return <div key={i} className={'copilot-msg ' + (msg.role === 'user' ? 'copilot-msg-user' : 'copilot-msg-ai')}>
                        {msg.content}
                        {msg.action && <span className="copilot-msg-action">{msg.action}</span>}
                    </div>;
                })}
            </div>}

            {messages.length === 0 && <div style={{marginBottom:'12px', padding:'12px', background:'#f7f7f7', border:'1px solid #e3e3e3', borderRadius:'12px'}}>
                <div style={{fontSize:'13px', fontWeight:'600', color:'#303030', marginBottom:'8px'}}>
                    <i className="fa fa-lightbulb-o" style={{marginRight:'4px'}}></i> {this.t('templateEditor.trySamplePrompts')}
                </div>
                <div style={{display:'flex', flexWrap:'wrap', gap:'4px'}}>
                    {[
                        'translate to German',
                        'show tax columns',
                        'add QR code',
                        'use teal palette',
                        'change font to Inter',
                        'add product images',
                        'make header more compact',
                        'add bank details',
                        'remove discount column',
                        'show shipping address',
                        'add barcode',
                        'make it modern'
                    ].map(function(sample) {
                        return <button key={sample} type="button"
                            style={{padding:'2px 10px', fontSize:'12px', lineHeight:'20px', background:'#fff', border:'1px solid #e3e3e3', borderRadius:'9999px', color:'#303030', cursor:'pointer', transition:'background-color 0.15s'}}
                            onMouseOver={function(e) { e.target.style.background='#f1f1f1'; }}
                            onMouseOut={function(e) { e.target.style.background='#fff'; }}
                            onClick={function() { this.setState({ copilotInput: sample }); }.bind(this)}
                        >{sample}</button>;
                    }.bind(this))}
                </div>
            </div>}

            <div className="copilot-input-row">
                <textarea
                    className="copilot-input"
                    value={input}
                    onChange={function(e) { this.setState({ copilotInput: e.target.value }); }.bind(this)}
                    onKeyDown={function(e) { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); this.sendCopilotMessage(); } }.bind(this)}
                    placeholder={this.t('templateEditor.copilotPlaceholder')}
                    rows={2}
                    disabled={loading}
                />
                <button type="button" className="copilot-send" onClick={this.sendCopilotMessage.bind(this)} disabled={loading || !input.trim()}>
                    <i className="fa fa-paper-plane"></i>
                </button>
            </div>

            <div style={{display:'flex', gap:'8px', margin:'8px 0'}}>
                <button type="button" className="copilot-reject" onClick={this.copilotUndo.bind(this)} disabled={!this.state.copilotCanUndo} title={this.t('templateEditor.undo')}>
                    <i className="fa fa-undo"></i> {this.t('templateEditor.undo')}
                </button>
                <button type="button" className="copilot-reject" onClick={this.copilotRedo.bind(this)} disabled={!this.state.copilotCanRedo} title={this.t('templateEditor.redo')}>
                    <i className="fa fa-repeat"></i> {this.t('templateEditor.redo')}
                </button>
            </div>

            {loading && <div className="copilot-loading-text">
                <span className="copilot-spinner"></span> {this.t('templateEditor.aiThinking')}
                <button type="button" className="copilot-reject" style={{marginLeft:'8px'}} onClick={this.abortCopilot.bind(this)}>{this.t('templateEditor.cancel')}</button>
            </div>}

            {proposal && <div className="copilot-proposal">
                <div className="copilot-proposal-title">
                    <i className="fa fa-check-circle" style={{marginRight:'4px'}}></i>
                    {proposal.explanation || this.t('templateEditor.proposedChanges')}
                </div>
                {proposal.reasoning && <div style={{fontSize:'11px',color:'#475569',fontStyle:'italic',marginBottom:'8px',paddingLeft:'4px'}}>
                    <i className="fa fa-lightbulb-o" style={{marginRight:'4px',color:'#eab308'}}></i>{proposal.reasoning}
                </div>}
                {proposal.edits && proposal.edits.length > 0 && proposal.edits.map(function(edit, i) {
                    return <div key={i} style={{marginBottom:'8px'}}>
                        <span style={{fontSize:'10px', fontWeight:'600', color:'#475569', textTransform:'uppercase'}}>{edit.target} template:</span>
                        <div className="copilot-proposal-code">
                            <span style={{color:'#f87171'}}>- {edit.search}</span>{'\n'}
                            <span style={{color:'#4ade80'}}>+ {edit.replace}</span>
                        </div>
                    </div>;
                })}
                {proposal.fullBody && <div style={{marginBottom:'8px'}}>
                    <span style={{fontSize:'10px', fontWeight:'600', color:'#475569'}}>BODY TEMPLATE (full replace):</span>
                    <div className="copilot-proposal-code">{proposal.fullBody.substring(0, 1000)}{proposal.fullBody.length > 1000 ? '\n...' : ''}</div>
                </div>}
                {proposal.fullRow && <div style={{marginBottom:'8px'}}>
                    <span style={{fontSize:'10px', fontWeight:'600', color:'#475569'}}>ROW TEMPLATE (full replace):</span>
                    <div className="copilot-proposal-code">{proposal.fullRow.substring(0, 500)}{proposal.fullRow.length > 500 ? '\n...' : ''}</div>
                </div>}
                <div className="copilot-actions">
                    <button type="button" className="copilot-apply" onClick={this.applyCopilotProposal.bind(this)}>
                        <i className="fa fa-check" style={{marginRight:'4px'}}></i> {this.t('templateEditor.applyChanges')}
                    </button>
                    <button type="button" className="copilot-reject" onClick={function() { this.setState({ copilotProposal: null }); }.bind(this)}>
                        {this.t('templateEditor.dismiss')}
                    </button>
                </div>
            </div>}


            <div className="copilot-engine-bar">
                <span className="copilot-engine-dot" style={{background: engineReady ? '#22c55e' : engineLoading ? '#f59e0b' : '#94a3b8'}}></span>
                {engineReady && <span>{this.t('templateEditor.aiEngineReady')}</span>}
                {engineLoading && <span id="copilot-progress-text">{this.t('templateEditor.loadingAiModel')}</span>}
                {!engineReady && !engineLoading && <span>
                    {this.t('templateEditor.aiEngineOff')} <a href="#" style={{color:'#005bd3'}} onClick={function(e) { e.preventDefault(); this.setState({ copilotShowModelConfirm: true }); }.bind(this)}>{this.t('templateEditor.loadForAdvanced')}</a>
                </span>}
                {engineLoading && <div className="copilot-progress-bar" style={{flex:1, margin:0}}>
                    <div id="copilot-progress-fill" className="copilot-progress-fill" style={{width:'0%'}}></div>
                </div>}
                <select
                    className="copilot-model-select"
                    value={state.copilotModel}
                    disabled={engineLoading}
                    onChange={function(e) {
                        var newModel = e.target.value;
                        this.setState({ copilotModel: newModel }, function() {
                            if (this.state.copilotEngineReady || this.copilotWorker) {
                                this.initCopilotEngine(true);
                            }
                        }.bind(this));
                    }.bind(this)}
                >
                    <option value="Qwen3-8B-q4f16_1-MLC">Qwen3 8B — best quality, 6GB+ VRAM</option>
                    <option value="Qwen3-4B-q4f16_1-MLC">Qwen3 4B — recommended, 4GB+ VRAM</option>
                    <option value="Qwen3.5-4B-q4f16_1-MLC">Qwen3.5 4B — alternative, 4GB+ VRAM</option>
                    <option value="Qwen2.5-Coder-1.5B-Instruct-q4f16_1-MLC">Qwen 2.5 Coder 1.5B — lightweight, 2GB+ VRAM</option>
                </select>
                {state.copilotModel === 'Qwen3-8B-q4f16_1-MLC' && !engineReady && <div style={{flexBasis: '100%', background: lowMemory ? '#fee8eb' : '#fff1e3', color: lowMemory ? '#8e0b21' : '#5e4200', borderRadius: '8px', fontSize: '12px', lineHeight: '16px', padding: '8px 12px'}}>
                    <i className="fa fa-exclamation-triangle" style={{marginRight:'4px'}}></i>
                    {lowMemory
                        ? 'Your device reports less than 8 GB RAM — Qwen3 8B may not run well. We recommend switching to Qwen3 4B or Qwen 2.5 1.5B below.'
                        : 'Qwen3 8B needs ~6 GB GPU memory (first load downloads ~5.7 GB). On low-memory devices, pick Qwen3 4B below.'}
                </div>}
            </div>

            <div style={{fontSize:'12px', color:'#616161', lineHeight:'1.6'}}>
                <i className="fa fa-bolt" style={{marginRight:'4px', color:'#f59e0b'}}></i>
                <strong>Instant:</strong> {this.t('templateEditor.instantCommands')}
                <br/>
                <span>
                    <i className="fa fa-rocket" style={{marginRight:'4px', color:'#4a4a4a'}}></i>
                    <strong>AI Engine:</strong> {this.t('templateEditor.aiEngineDesc')}
                    <br/>
                    <i className="fa fa-info-circle" style={{marginRight:'4px', color:'#64748b'}}></i>
                    {this.t('templateEditor.aiLocalDesc')}
                </span>
            </div>
        </div>;
    }

    render() {
        let { el, theme, idx, user, fields, onClick, dispatch } = this.props;
        const { activeTab } = this.state;
        const t = (id) => this.t(id);

        if (!theme)
            return <div></div>

        const tabs = [
            { id: 'style', label: t('templateEditor.tabStyle'), icon: 'fa-paint-brush' },
            { id: 'content', label: t('templateEditor.tabContent'), icon: 'fa-list-alt' },
            { id: 'translations', label: t('templateEditor.tabTranslations'), icon: 'fa-language' },
            { id: 'paper', label: t('templateEditor.tabSettings'), icon: 'fa-cog' },
            { id: 'code', label: t('templateEditor.tabCode'), icon: 'fa-code' },
            { id: 'copilot', label: t('templateEditor.tabCopilot'), icon: 'fa-rocket' },
        ];

        return (<div>
            <style dangerouslySetInnerHTML={{__html: `
                .editor-tabs-wrap { position: relative; }
                .editor-card { background: #fff; border: 1px solid #e3e3e3; border-radius: 12px; box-shadow: 0 1px 0 rgba(26,26,26,0.07); padding: 16px; }
                .editor-tabs-wrap .editor-tabs-arrow { position: absolute; top: 0; bottom: 13px; width: 28px; display: none; align-items: center; justify-content: center; color: #616161; font-size: 16px; font-weight: 700; cursor: pointer; z-index: 2; border: none; padding: 0; }
                .editor-tabs-wrap .editor-tabs-arrow:hover { color: #303030; }
                .editor-tabs-wrap .editor-tabs-arrow.arrow-right { right: 0; background: linear-gradient(to right, transparent, #fff 50%); }
                .editor-tabs-wrap .editor-tabs-arrow.arrow-left { left: 0; background: linear-gradient(to left, transparent, #fff 50%); }
                .editor-tabs-wrap.has-overflow-right .editor-tabs-arrow.arrow-right { display: flex; }
                .editor-tabs-wrap.has-overflow-left .editor-tabs-arrow.arrow-left { display: flex; }
                .editor-tabs { display: flex; gap: 4px; border-bottom: 1px solid #e3e3e3; margin-bottom: 0; padding: 0 0 12px; overflow-x: auto; white-space: nowrap; -webkit-overflow-scrolling: touch; scrollbar-width: none; }
                .editor-tabs::-webkit-scrollbar { display: none; }
                .editor-tab { padding: 6px 12px; font-size: 13px; line-height: 20px; font-weight: 500; color: #616161; cursor: pointer; border-radius: 8px; transition: background-color 0.15s, color 0.15s; display: flex; align-items: center; gap: 6px; flex-shrink: 0; }
                .editor-tab:hover { color: #303030; background: rgba(0,0,0,0.05); }
                .editor-tab.active { color: #303030; background: rgba(0,0,0,0.08); font-weight: 600; }
                .editor-tab .fa { font-size: 11px; }
                .editor-tab-content { padding: 16px 0; }
                .editor-section { margin-bottom: 20px; padding-bottom: 16px; border-bottom: 1px solid #ebebeb; }
                .editor-section:last-child { border-bottom: none; }
                .editor-section-title { font-size: 13px; font-weight: 650; color: #303030; margin-bottom: 10px; }
                .palette-strip { display: flex; flex-wrap: wrap; gap: 4px; margin-bottom: 10px; }
                .palette-chip { display: flex; width: 44px; height: 18px; border-radius: 3px; overflow: hidden; cursor: pointer; border: 1.5px solid #e2e8f0; transition: all 0.12s; flex-shrink: 0; }
                .palette-chip:hover { transform: scale(1.1); box-shadow: 0 2px 6px rgba(0,0,0,0.15); border-color: #94a3b8; }
                .palette-chip.active { border-color: #303030; box-shadow: 0 0 0 1.5px #303030; }
                .palette-chip span { flex: 1; height: 100%; }
                .color-pickers-inline { display: flex; flex-wrap: wrap; gap: 6px; align-items: flex-start; }
                .color-picker-item { display: flex; flex-direction: column; align-items: center; gap: 2px; }
                .color-label { font-size: 12px; color: #616161; font-weight: 500; }
                .toggle-list { display: flex; flex-direction: column; gap: 6px; }
                .toggle-item { display: flex; align-items: center; gap: 8px; font-size: 13px; color: #303030; cursor: pointer; padding: 5px 8px; border-radius: 4px; margin: 0; }
                .toggle-item:hover { background: #f7f7f7; }
                .toggle-item input[type="checkbox"] { width: 15px; height: 15px; accent-color: #303030; }
                .editor-panel { min-height: 720px; max-height: 720px; overflow-x: hidden; overflow-y: auto; padding-right: 12px; }
                .template-reference code { display: inline-block; background: #f1f5f9; padding: 1px 5px; border-radius: 2px; font-size: 10.5px; margin: 1px 0; }
                .template-reference h5 { margin: 12px 0 6px; font-size: 12px; font-weight: 600; color: #334155; }
            `}}></style>


            <div className="row editor-layout">
                <div className="col-sm-12 col-md-6 col-lg-6">
                    <div className="editor-card">
                        <div className="editor-tabs-wrap">
                            <button type="button" className="editor-tabs-arrow arrow-left" onClick={function() {
                                if (this._tabsEl) this._tabsEl.scrollBy({ left: -120, behavior: 'smooth' });
                            }.bind(this)}>&#8249;</button>
                            <div className="editor-tabs" ref={function(el) {
                                this._tabsEl = el;
                                if (el && !el._scrollListener) {
                                    var wrap = el.parentNode;
                                    var check = function() {
                                        var hasOverflow = el.scrollWidth > el.clientWidth;
                                        var atStart = el.scrollLeft <= 2;
                                        var atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 2;
                                        wrap.classList.toggle('has-overflow-right', hasOverflow && !atEnd);
                                        wrap.classList.toggle('has-overflow-left', hasOverflow && !atStart);
                                    };
                                    el.addEventListener('scroll', check);
                                    setTimeout(check, 0);
                                    el._scrollListener = true;
                                }
                            }.bind(this)}>
                                {tabs.map(t => (
                                    <span key={t.id} className={activeTab === t.id ? 'editor-tab active' : 'editor-tab'} onClick={() => this.setTab(t.id)}>
                                        <i className={`fa ${t.icon}`}></i> {t.label}
                                    </span>
                                ))}
                            </div>
                            <button type="button" className="editor-tabs-arrow arrow-right" onClick={function() {
                                if (this._tabsEl) this._tabsEl.scrollBy({ left: 120, behavior: 'smooth' });
                            }.bind(this)}>&#8250;</button>
                        </div>
                        <div className="editor-panel">
                            {activeTab === 'style' && this.renderStyleTab()}
                            {activeTab === 'content' && this.renderContentTab()}
                            {activeTab === 'translations' && this.renderTranslationsTab()}
                            {activeTab === 'paper' && this.renderPaperTab()}
                            {activeTab === 'code' && this.renderCodeTab()}
                            {activeTab === 'copilot' && this.renderCopilotTab()}
                        </div>
                    </div>
                </div>
                <div className="col-sm-12 col-md-6 col-lg-6">
                    <ThemePreview height="780px" theme={theme} />
                </div>
            </div>
        </div>)
    }
}

export default Theme;



// WEBPACK FOOTER //
// app/containers/Theme.jsx